import useUrlSearchState from "@/hooks/useUrlSearchState";
import React, { Component, useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import {
  ArrowLeft,
  Beaker,
  BookOpen,
  Check,
  ChevronRight,
  Clock,
  Crown,
  Dumbbell,
  Film,
  Gamepad2,
  Globe,
  Lock,
  MapPin,
  Monitor,
  Music,
  ExternalLink,
  Palette,
  PawPrint,
  Play,
  Search,
  Star,
  Sword,
  Trash2,
  ThumbsDown,
  ThumbsUp,
  Trophy,
  Tv,
  Users,
  X as XIcon,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { isAdminUser } from "@/lib/userAccess";
import { isOnlineGameEvent } from "@/lib/eventClassification";
import { usePlatform } from "@/contexts/PlatformContext";
import { AuthModal } from "@/components/auth/AuthModal";
import GamesCatalogSections from "@/components/game/GamesCatalogSections";
import SpecialGameActions from "@/components/game/SpecialGameActions";
import PreGameCover from "@/components/game/PreGameCover";
import Leaderboard from "@/components/game/Leaderboard";
import { useCMSPage } from "@/components/cms/DynamicPage";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useQuizContentProtection from "@/hooks/useQuizContentProtection";
import { trackMetaEvent } from "@/lib/metaPixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const isEventCancelled = (event) => ["cancelled", "canceled"].includes(String(event?.status || "").toLowerCase());
const APP_CATALOG_PAGE_SIZE = 12;
const SPECIAL_GAMES_PAGE_SIZE = 6;

const DEFAULT_THEME_ICONS = {
  Musica: "music",
  Esportes: "dumbbell",
  Historia: "book",
  Geografia: "map-pin",
  Ciencias: "beaker",
  "Mundo Animal": "paw",
  Cinema: "film",
  "Series de TV": "tv",
  "Desenhos Animados": "palette",
  Quadrinhos: "book",
  Games: "monitor",
};

const ICON_COMPONENTS = {
  globe: Globe,
  music: Music,
  dumbbell: Dumbbell,
  book: BookOpen,
  "map-pin": MapPin,
  beaker: Beaker,
  paw: PawPrint,
  film: Film,
  tv: Tv,
  palette: Palette,
  monitor: Monitor,
  star: Star,
  sword: Sword,
};


const normalizeComparableText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

function matchesCatalogSearch(searchTerm, ...values) {
  const normalizedTerm = normalizeComparableText(searchTerm);
  if (!normalizedTerm) return true;

  return normalizeComparableText(values.flat(Infinity).filter(Boolean).join(" ")).includes(normalizedTerm);
}

function AppCatalogSearch({ value, onChange, label }) {
  return (
    <label className="relative block">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={`Buscar em ${label}`}
        className="h-11 w-full rounded-xl border border-white/10 bg-black/30 pl-10 pr-3 font-outfit text-sm text-white outline-none placeholder:text-zinc-600 focus:border-[#fccf1f]/60"
      />
    </label>
  );
}

function InfiniteCatalogLoader({ hasMore, onLoadMore }) {
  const targetRef = useRef(null);

  useEffect(() => {
    const target = targetRef.current;
    if (!target || !hasMore || typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) onLoadMore();
    }, { rootMargin: "180px" });
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore]);

  if (!hasMore) return null;

  return (
    <div ref={targetRef} className="py-3 text-center font-outfit text-xs text-zinc-500">
      Carregando mais jogos...
    </div>
  );
}

function GamePassPurchaseDialog({ open, onClose, wallet, buyingPackId, onBuy, returnTo = "/jogos" }) {
  const packs = wallet?.packs || [];
  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="max-w-lg border-white/10 bg-zinc-950 text-white">
        <DialogHeader>
          <DialogTitle className="font-nunito text-2xl font-black text-[#fccf1f]">Passes de Jogo</DialogTitle>
          <DialogDescription className="font-outfit text-zinc-400">
            Cada jogo informa quantos passes são necessários para desbloqueá-lo. Depois de desbloqueado, você pode jogar novamente sem gastar novos passes. Este saldo é exclusivo para jogos digitais e não altera seus créditos de ingresso para eventos presenciais.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-2xl border border-[#fccf1f]/20 bg-[#fccf1f]/8 px-4 py-3 font-outfit text-sm text-zinc-200">
          Seu saldo atual: <strong className="text-[#fccf1f]">{wallet?.balance || 0} {wallet?.balance === 1 ? "passe" : "passes"}</strong>
        </div>
        <div className="space-y-3">
          {packs.map((pack) => {
            const price = Number(pack.price || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
            const unitPrice = Number(pack.price || 0) / Math.max(1, Number(pack.passes || 1));
            const reference = Number((packs.find((item) => Number(item.passes) === 1)?.price) || unitPrice) * Number(pack.passes || 1);
            const savings = Math.max(0, reference - Number(pack.price || 0));
            return (
              <div key={pack.id} className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div>
                  <p className="font-nunito text-base font-black text-white">{pack.name}</p>
                  <p className="mt-1 font-outfit text-sm text-zinc-400">{price}</p>
                  {savings > 0.005 ? <p className="mt-1 font-outfit text-xs font-semibold text-emerald-300">Economize {savings.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</p> : null}
                </div>
                <Button type="button" onClick={() => onBuy(pack.id, returnTo)} disabled={buyingPackId === pack.id} className="shrink-0 bg-[#fccf1f] font-bold text-black hover:bg-[#e6bc1c]">
                  {buyingPackId === pack.id ? "Abrindo..." : "Comprar"}
                </Button>
              </div>
            );
          })}
          {!packs.length ? <p className="rounded-xl border border-dashed border-white/10 p-4 text-center font-outfit text-sm text-zinc-400">Nenhum pacote está disponível no momento.</p> : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const getDifficultySnapshotPresentation = (snapshot) => {
  return {
    label: snapshot?.label || "Novo",
  };
};

const normalizeGamesGridConfig = (content = {}) => ({
  title: content?.title || "Jogos Online",
  subtitle: content?.subtitle || "Escolha um tema, veja quantas perguntas te esperam e comece a jogar na hora.",
  maxItems: Number.isFinite(Number(content?.maxItems)) ? Math.max(1, Number(content.maxItems)) : null,
  mobileColumns: Math.min(4, Math.max(1, parseInt(content?.mobileColumns, 10) || 2)),
  tabletColumns: Math.min(6, Math.max(2, parseInt(content?.tabletColumns, 10) || 4)),
  desktopColumns: Math.min(8, Math.max(3, parseInt(content?.desktopColumns, 10) || 8)),
  themeIcons: content?.themeIcons && typeof content.themeIcons === "object" ? content.themeIcons : {},
  sectionOrder: (() => {
    const allowedSections = ["community", "online", "special", "premium", "presencial"];
    const configured = Array.isArray(content?.sectionOrder) && content.sectionOrder.length
      ? content.sectionOrder.filter((item) => allowedSections.includes(item))
      : ["online", "special", "premium", "community", "presencial"];
    if (configured.includes("premium")) return configured;
    const specialIndex = configured.indexOf("special");
    const onlineIndex = configured.indexOf("online");
    const insertAfter = specialIndex >= 0 ? specialIndex : onlineIndex;
    return [
      ...configured.slice(0, insertAfter + 1),
      "premium",
      ...configured.slice(insertAfter + 1),
    ];
  })(),
  communitySection: {
    title: content?.communitySection?.title || "Quizzes da Comunidade",
    subtitle: content?.communitySection?.subtitle || "Jogue quizzes criados por outros jogadores e descubra novos estilos de rodada.",
    emptyText: content?.communitySection?.emptyText || "Ainda não há quizzes publicados pela comunidade.",
    creatorDescription: content?.communitySection?.creatorDescription || "Agora os assinantes podem montar quizzes autorais com até 10 perguntas próprias, gerenciar tudo em Meus Quizzes e enviar perguntas para aprovação no banco geral.",
    creatorButtonLabel: content?.communitySection?.creatorButtonLabel || "Ir para Meus Quizzes",
  },
  onlineSection: {
    title: content?.onlineSection?.title || content?.title || "Jogos Online",
    subtitle: content?.onlineSection?.subtitle || content?.subtitle || "Escolha um tema, veja quantas perguntas te esperam e comece a jogar sem passar por etapas extras.",
    emptyText: content?.onlineSection?.emptyText || "Carregando temas...",
  },
  specialSection: {
    title: content?.specialSection?.title || "Jogos Especiais",
    subtitle: content?.specialSection?.subtitle || "",
    emptyText: content?.specialSection?.emptyText || "No momento não há jogos especiais agendados. Mas fique ligado, porque já já nossos Elfos Domésticos vão lançar um feitiço e um novo jogo surgirá!",
  },
  premiumSection: {
    title: content?.premiumSection?.title || "Jogos Premium",
    subtitle: content?.premiumSection?.subtitle || "Eventos online com experiências ao vivo e acesso exclusivo.",
    emptyText: content?.premiumSection?.emptyText || "Nenhum jogo premium ativo no momento.",
  },
  presencialSection: {
    title: content?.presencialSection?.title || "Eventos Presenciais",
    subtitle: content?.presencialSection?.subtitle || "Procurando jogos presenciais? Fique ligado nesses eventos:",
    emptyText: content?.presencialSection?.emptyText || "Nenhum evento presencial agendado no momento.",
    ctaLabel: content?.presencialSection?.ctaLabel || "Ver todos os eventos presenciais",
  },
});

const MOBILE_GRID_CLASS = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
};

const TABLET_GRID_CLASS = {
  2: "sm:grid-cols-2 md:grid-cols-2",
  3: "sm:grid-cols-3 md:grid-cols-3",
  4: "sm:grid-cols-3 md:grid-cols-4",
  5: "sm:grid-cols-3 md:grid-cols-5",
  6: "sm:grid-cols-3 md:grid-cols-6",
};

const DESKTOP_GRID_CLASS = {
  3: "xl:grid-cols-3 2xl:grid-cols-3",
  4: "xl:grid-cols-4 2xl:grid-cols-4",
  5: "xl:grid-cols-5 2xl:grid-cols-5",
  6: "xl:grid-cols-6 2xl:grid-cols-6",
  7: "xl:grid-cols-6 2xl:grid-cols-7",
  8: "xl:grid-cols-6 2xl:grid-cols-8",
};

const getThemeIconName = (themeName, themeIconsConfig) => {
  const normalizedThemeName = normalizeComparableText(themeName);
  const customMatch = Object.entries(themeIconsConfig || {}).find(
    ([configuredTheme]) => normalizeComparableText(configuredTheme) === normalizedThemeName
  );
  if (customMatch) return customMatch[1];

  const defaultMatch = Object.entries(DEFAULT_THEME_ICONS).find(
    ([configuredTheme]) => normalizeComparableText(configuredTheme) === normalizedThemeName
  );
  return defaultMatch?.[1] || "star";
};

const DEFAULT_TIME_PER_QUESTION = 30;
const QUESTION_TYPE_SHORT_ANSWER = "short_answer";
const QUESTION_TYPE_NUMERIC_RANGE = "numeric_range";
const QUESTION_TYPE_TRUE_FALSE = "true_false";
const QUESTION_TYPE_IMAGE_PIN = "image_pin";
const QUESTION_MEDIA_TYPE_IMAGE = "image";
const QUESTION_MEDIA_TYPE_AUDIO = "audio";
const QUESTION_MEDIA_TYPE_VIDEO = "video";
const DIRECT_AUDIO_EXTENSIONS = [".mp3", ".wav", ".ogg", ".oga", ".m4a", ".aac"];
const DIRECT_VIDEO_EXTENSIONS = [".mp4", ".webm", ".mov", ".m4v", ".ogv"];

const calculateQuestionPoints = (isCorrect, timeTakenMs, totalTimeMs) => {
  if (!isCorrect) return 0;
  if (!totalTimeMs || totalTimeMs <= 1000) return 1000;
  const clamped = Math.max(0, Math.min(timeTakenMs, totalTimeMs));
  if (clamped <= 1000) return 1000;
  const scoringWindowMs = Math.max(1, totalTimeMs - 1000);
  const remainingFraction = (totalTimeMs - clamped) / scoringWindowMs;
  return 500 + Math.floor(remainingFraction * 500);
};

const parseNumericAnswer = (value) => {
  const normalized = String(value ?? "").trim().replace(",", ".");
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const countDecimalPlaces = (value) => {
  const normalized = String(value ?? "").trim().replace(",", ".");
  if (!normalized.includes(".")) return 0;
  return normalized.split(".")[1].replace(/0+$/, "").length;
};

const getNumericSliderStep = (...values) => {
  const decimalPlaces = Math.max(...values.map(countDecimalPlaces), 0);
  if (decimalPlaces <= 0) return 1;
  return 1 / (10 ** decimalPlaces);
};

const clampNumericValue = (value, min, max) => Math.min(Math.max(value, min), max);

const alignNumericValueToStep = (value, min, step) => {
  if (!Number.isFinite(value) || !Number.isFinite(min) || !Number.isFinite(step) || step <= 0) {
    return value;
  }
  const steps = Math.round((value - min) / step);
  return min + (steps * step);
};

const formatNumericDisplay = (value, step = 1) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "";
  const decimals = countDecimalPlaces(step);
  if (decimals <= 0) return String(Math.round(numericValue));
  return numericValue.toFixed(decimals).replace(/\.?0+$/, "");
};

const getPathnameFromUrl = (url) => {
  try {
    return new URL(url).pathname.toLowerCase();
  } catch {
    return "";
  }
};

const formatDateBR = (value) => {
  if (!value) return "Data a confirmar";
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const isDirectAudioUrl = (url) => {
  const pathname = getPathnameFromUrl(url);
  return DIRECT_AUDIO_EXTENSIONS.some((extension) => pathname.endsWith(extension));
};

const isDirectVideoUrl = (url) => {
  const pathname = getPathnameFromUrl(url);
  return DIRECT_VIDEO_EXTENSIONS.some((extension) => pathname.endsWith(extension));
};

const getYouTubeEmbedUrl = (url) => {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, "").toLowerCase();
    if (host === "youtu.be") {
      const videoId = parsed.pathname.replace(/\//g, "");
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }
    if (host.endsWith("youtube.com")) {
      const videoId = parsed.searchParams.get("v");
      if (videoId) return `https://www.youtube.com/embed/${videoId}`;
      const match = parsed.pathname.match(/\/embed\/([^/?#]+)/i) || parsed.pathname.match(/\/shorts\/([^/?#]+)/i);
      return match?.[1] ? `https://www.youtube.com/embed/${match[1]}` : null;
    }
  } catch {
    return null;
  }
  return null;
};

const getVimeoEmbedUrl = (url) => {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, "").toLowerCase();
    if (!host.endsWith("vimeo.com")) return null;
    const match = parsed.pathname.match(/\/(\d+)/);
    return match?.[1] ? `https://player.vimeo.com/video/${match[1]}` : null;
  } catch {
    return null;
  }
  return null;
};

const buildDirectMediaUrl = (url, startSeconds = null, endSeconds = null) => {
  if (!url) return "";
  const hasStart = Number.isFinite(startSeconds) && startSeconds >= 0;
  const hasEnd = Number.isFinite(endSeconds) && endSeconds > 0;
  if (!hasStart && !hasEnd) return url;
  const fragment = hasEnd ? `#t=${hasStart ? startSeconds : 0},${endSeconds}` : `#t=${startSeconds}`;
  return `${url}${fragment}`;
};

const getTimedYouTubeEmbedUrl = (url, startSeconds = null, endSeconds = null) => {
  const baseUrl = getYouTubeEmbedUrl(url);
  if (!baseUrl) return null;
  try {
    const parsed = new URL(baseUrl);
    if (Number.isFinite(startSeconds) && startSeconds >= 0) {
      parsed.searchParams.set("start", String(Math.floor(startSeconds)));
    }
    if (Number.isFinite(endSeconds) && endSeconds > 0) {
      parsed.searchParams.set("end", String(Math.floor(endSeconds)));
    }
    return parsed.toString();
  } catch {
    return baseUrl;
  }
};

const getTimedVimeoEmbedUrl = (url, startSeconds = null) => {
  const baseUrl = getVimeoEmbedUrl(url);
  if (!baseUrl) return null;
  if (!Number.isFinite(startSeconds) || startSeconds < 0) return baseUrl;
  return `${baseUrl}#t=${Math.floor(startSeconds)}s`;
};

const QUESTION_TYPE_LABELS = {
  [QUESTION_TYPE_TRUE_FALSE]: "Real ou Fake News",
  [QUESTION_TYPE_SHORT_ANSWER]: "Resposta Curta",
  [QUESTION_TYPE_NUMERIC_RANGE]: "Faixa Numérica",
  [QUESTION_TYPE_IMAGE_PIN]: "Image Pin",
  default: "Múltipla Escolha",
};

const QUESTION_TYPE_HELPERS = {
  [QUESTION_TYPE_TRUE_FALSE]: "Leia com atenção, escolha um lado e confira o contexto completo depois da resposta.",
  [QUESTION_TYPE_SHORT_ANSWER]: "Vale responder do seu jeito. O sistema considera variações aceitas e pequenos erros de digitação.",
  [QUESTION_TYPE_NUMERIC_RANGE]: "Use o controle para posicionar seu palpite e envie quando chegar no número desejado.",
  [QUESTION_TYPE_IMAGE_PIN]: "Observe a imagem com calma, marque o ponto exato e só depois envie.",
  default: "Escolha a alternativa que melhor responde à pergunta antes do tempo acabar.",
};

const levenshteinDistance = (left, right) => {
  if (left === right) return 0;
  if (!left) return right.length;
  if (!right) return left.length;
  let previousRow = Array.from({ length: right.length + 1 }, (_, index) => index);
  for (let i = 1; i <= left.length; i += 1) {
    const currentRow = [i];
    for (let j = 1; j <= right.length; j += 1) {
      const insertions = previousRow[j] + 1;
      const deletions = currentRow[j - 1] + 1;
      const substitutions = previousRow[j - 1] + (left[i - 1] === right[j - 1] ? 0 : 1);
      currentRow.push(Math.min(insertions, deletions, substitutions));
    }
    previousRow = currentRow;
  }
  return previousRow[right.length];
};

const isLenientShortAnswerMatch = (submittedText, expectedText) => {
  if (!submittedText || !expectedText) return false;
  if (submittedText === expectedText) return true;
  const maxLen = Math.max(submittedText.length, expectedText.length);
  if (maxLen <= 4) return false;
  const allowedDistance = maxLen <= 8 ? 1 : 2;
  return levenshteinDistance(submittedText, expectedText) <= allowedDistance;
};

const getAcceptedShortAnswers = (question) => {
  const acceptedAnswers = [question?.answer_text, ...(question?.options || [])]
    .map((option) => String(option || "").trim())
    .filter(Boolean);
  return Array.from(new Set(acceptedAnswers));
};

const normalizeImagePinPoint = (point) => {
  if (!point || typeof point !== "object") return null;
  const x = Number(point.x);
  const y = Number(point.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  if (x < 0 || x > 1 || y < 0 || y > 1) return null;
  return { x, y };
};

const normalizeImagePinPoints = (points) =>
  Array.isArray(points)
    ? points
      .map((point) => normalizeImagePinPoint(point))
      .filter(Boolean)
    : [];

const imagePinPointsToSvg = (points) =>
  normalizeImagePinPoints(points)
    .map((point) => `${point.x * 100},${point.y * 100}`)
    .join(" ");

const isPointInsidePolygon = (point, polygon) => {
  const normalizedPoint = normalizeImagePinPoint(point);
  const normalizedPolygon = normalizeImagePinPoints(polygon);
  if (!normalizedPoint || normalizedPolygon.length < 3) return false;

  let inside = false;
  for (let i = 0, j = normalizedPolygon.length - 1; i < normalizedPolygon.length; j = i, i += 1) {
    const xi = normalizedPolygon[i].x;
    const yi = normalizedPolygon[i].y;
    const xj = normalizedPolygon[j].x;
    const yj = normalizedPolygon[j].y;
    const intersects = ((yi > normalizedPoint.y) !== (yj > normalizedPoint.y))
      && (
        normalizedPoint.x
        < (((xj - xi) * (normalizedPoint.y - yi)) / ((yj - yi) || Number.EPSILON)) + xi
      );
    if (intersects) inside = !inside;
  }
  return inside;
};

const didAnswerDetailMatchQuestion = (question, detail) => {
  if (!question || !detail) return false;

  if (question.question_type === QUESTION_TYPE_SHORT_ANSWER) {
    const submittedText = normalizeComparableText(detail.selected_text);
    return getAcceptedShortAnswers(question)
      .map((option) => normalizeComparableText(option))
      .some((expected) => submittedText && (submittedText === expected || isLenientShortAnswerMatch(submittedText, expected)));
  }

  if (question.question_type === QUESTION_TYPE_NUMERIC_RANGE) {
    return parseNumericAnswer(detail.selected_text) === parseNumericAnswer(question.answer_text);
  }

  if (question.question_type === QUESTION_TYPE_IMAGE_PIN) {
    return isPointInsidePolygon(detail.selected_point, normalizeImagePinPoints(question.image_pin_points));
  }

  return detail.selected_answer === question.correct_answer;
};


class GamesPageErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: "" };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      errorMessage: error?.message || "Ocorreu um erro inesperado ao carregar a página de jogos.",
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error("GamesPage render error", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen pt-28 pb-16">
          <div className="container mx-auto max-w-3xl px-4 md:px-6">
            <div className="rounded-3xl border border-red-500/20 bg-red-500/10 p-6 text-red-100">
              <h1 className="font-nunito text-2xl font-black text-white">Não foi possível carregar Jogos</h1>
              <p className="mt-3 text-sm text-red-100/90">
                Encontramos um erro ao renderizar esta página. Você pode tentar recarregar agora.
              </p>
              <p className="mt-2 rounded-xl border border-white/10 bg-black/20 px-4 py-3 font-mono text-xs text-red-100/80">
                {this.state.errorMessage}
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Button type="button" onClick={() => window.location.reload()} className="bg-white text-red-700 hover:bg-red-50">
                  Recarregar página
                </Button>
                <Link to="/">
                  <Button type="button" variant="outline" className="border-white/15 text-white hover:bg-white/5">
                    Voltar ao início
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function AppGamesHub({
  appGamesPanel,
  gamesRemainingToday,
  isUnlimitedDay,
  communityQuizzes,
  onlineEvents,
  specialEvents,
  specialGamesHasMore,
  specialGamesLoading,
  specialGamesQuery,
  openOnlineEventPurchase,
  presencialEvents,
  premiumNewCount,
  unseenPresencialCount,
  setAppGamesPanel,
  setShowAuth,
  setSpecialGamesQuery,
  startGame,
  startCommunityQuiz,
  startPremiumGame,
  startSpecialGame,
  user,
  userCanAccessOnlineEvent,
  hasAvailableGamePassForEvent,
  loadMoreSpecialGames,
  allThemes,
  eventNeedsTicket,
  eventUsesGamePass,
  getGamePassCost,
  getOnlineEventPath,
  navigate,
}) {
  const [searchTerms, setSearchTerms] = useUrlSearchState("busca", { online: "", special: "", premium: "", presenciais: "" });
  const [communityVisibleCount, setCommunityVisibleCount] = useState(APP_CATALOG_PAGE_SIZE);
  const [premiumVisibleCount, setPremiumVisibleCount] = useState(APP_CATALOG_PAGE_SIZE);
  const [presencialVisibleCount, setPresencialVisibleCount] = useState(APP_CATALOG_PAGE_SIZE);

  const searchTerm = appGamesPanel === "special" ? specialGamesQuery : (searchTerms[appGamesPanel] || "");
  const setSearchTerm = (value) => {
    if (appGamesPanel === "special") {
      setSpecialGamesQuery(value);
      return;
    }
    setSearchTerms((current) => ({ ...current, [appGamesPanel]: value }));
  };
  const filteredCommunityQuizzes = useMemo(() => communityQuizzes.filter((quiz) => matchesCatalogSearch(
    searchTerms.online,
    quiz.title,
    quiz.description,
    quiz.creator_display_name,
    quiz.creator_name,
    quiz.themes
  )), [communityQuizzes, searchTerms.online]);
  const filteredThemes = useMemo(() => allThemes.filter((theme) => theme.available && matchesCatalogSearch(
    searchTerms.online,
    theme.name,
    theme.category_name
  )), [allThemes, searchTerms.online]);
  const filteredPremiumEvents = useMemo(() => onlineEvents.filter((event) => matchesCatalogSearch(
    searchTerms.premium,
    event.title,
    event.description,
    event.quiz_themes
  )), [onlineEvents, searchTerms.premium]);
  const filteredSpecialEvents = specialEvents;
  const filteredPresencialEvents = useMemo(() => presencialEvents.filter((event) => matchesCatalogSearch(
    searchTerms.presenciais,
    event.title,
    event.description,
    event.location,
    event.city,
    event.venue?.name,
    event.venue?.city
  )), [presencialEvents, searchTerms.presenciais]);

  const getPassAction = (event) => {
    const needsUnlock = eventNeedsTicket(event) && !userCanAccessOnlineEvent(event);
    const usesPass = eventUsesGamePass(event);
    const canUsePass = needsUnlock && usesPass && hasAvailableGamePassForEvent(event);
    const paused = needsUnlock && event.ticket_sales_paused;
    const cost = getGamePassCost(event);
    return { needsUnlock, usesPass, canUsePass, paused, cost };
  };

  useEffect(() => setCommunityVisibleCount(APP_CATALOG_PAGE_SIZE), [searchTerms.online]);
  useEffect(() => setPremiumVisibleCount(APP_CATALOG_PAGE_SIZE), [searchTerms.premium]);
  useEffect(() => setPresencialVisibleCount(APP_CATALOG_PAGE_SIZE), [searchTerms.presenciais]);

  const visibleCommunityQuizzes = filteredCommunityQuizzes.slice(0, communityVisibleCount);
  const visiblePremiumEvents = filteredPremiumEvents.slice(0, premiumVisibleCount);
  const visibleSpecialEvents = filteredSpecialEvents;
  const visiblePresencialEvents = filteredPresencialEvents.slice(0, presencialVisibleCount);
  const menuItems = [
    { key: "online", label: "Online" },
    { key: "special", label: "Especiais" },
    { key: "premium", label: "Premium", badge: premiumNewCount },
    { key: "presenciais", label: "Presenciais", badge: unseenPresencialCount },
  ];

  return (
    <div className="min-h-screen bg-[#050505] pt-[4.4rem] pb-24" data-testid="app-games-page">
      <div className="mx-auto flex max-w-md flex-col gap-3 px-3">
        <div className="sticky top-16 z-30 -mx-3 border-b border-white/10 bg-[#050505]/95 px-3 pb-3 pt-1 backdrop-blur-xl">
          <div className="grid grid-cols-4 gap-2">
            {menuItems.map((item) => {
              const isActive = appGamesPanel === item.key;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setAppGamesPanel(item.key)}
                  className={`relative rounded-2xl border px-2 py-3 text-[10px] font-bold uppercase tracking-[0.08em] transition-all ${
                    isActive
                      ? "border-purple-500 bg-purple-600 text-white shadow-[0_10px_24px_rgba(147,51,234,0.3)]"
                      : "border-purple-500/50 bg-zinc-950 text-zinc-200"
                  }`}
                >
                  {item.label}
                  {item.badge > 0 ? (
                    <span className="absolute -right-1.5 -top-1.5 inline-flex min-w-[1.15rem] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-black leading-none text-white">
                      {item.badge}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        <section className="rounded-[22px] border border-white/10 bg-zinc-950/75 p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate font-nunito text-base font-black text-white">
                {user?.display_name || user?.name || "Jogador"}
              </p>
              <p className="font-outfit text-xs text-zinc-500">
                {user?.subscription_plan === "master"
                  ? "Lorde Supremo"
                  : user?.subscription_plan === "pro"
                  ? "Quizzeiro Pro"
                  : "Jogador Casual"}
              </p>
            </div>
            {user?.subscription_plan === "free" ? (
              <Link to="/clube">
                <Button size="sm" className="h-9 rounded-xl bg-[#fccf1f] px-3 text-[11px] font-bold uppercase text-black hover:bg-[#e6bc1c]">
                  <Crown className="mr-1 h-3.5 w-3.5" /> Upgrade
                </Button>
              </Link>
            ) : null}
          </div>

          <div className="mt-3 rounded-2xl border border-[#fccf1f]/20 bg-[#fccf1f]/8 px-3.5 py-3">
            <p className="font-outfit text-sm text-[#fccf1f]">
              {isUnlimitedDay ? (
                <>Você tem <span className="font-bold text-white">jogos ilimitados</span> hoje</>
              ) : (
                <>Você tem <span className="font-bold text-white">{gamesRemainingToday}</span> jogos disponíveis hoje</>
              )}
            </p>
            {!isUnlimitedDay && gamesRemainingToday <= 0 ? (
              <div className="mt-2 flex items-center gap-2 text-sm">
                <span className="font-outfit text-purple-300">Volte amanhã ou</span>
                <Link
                  to="/clube"
                  className="rounded-full border border-purple-500/40 bg-purple-500/10 px-3 py-1 font-outfit text-xs font-semibold uppercase tracking-[0.12em] text-purple-200"
                >
                  Acesse o Quiz Club
                </Link>
              </div>
            ) : null}
          </div>
        </section>

        <AppCatalogSearch
          value={searchTerm}
          onChange={setSearchTerm}
          label={appGamesPanel === "online" ? "jogos online" : appGamesPanel === "special" ? "jogos especiais" : appGamesPanel === "premium" ? "jogos premium" : "eventos presenciais"}
        />

        {appGamesPanel === "online" ? (
          <section className="space-y-3" data-catalog-palette="online">
            <div className="rounded-[22px] border border-white/10 bg-zinc-950/75 p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2 className="font-nunito text-lg font-black uppercase tracking-[0.08em] text-white">
                Jogos Gratuitos
              </h2>
              <span className="font-outfit text-[11px] uppercase tracking-[0.16em] text-zinc-500">
                {filteredThemes.length} temas
              </span>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {filteredThemes.map((theme) => {
                  const ThemeIcon = ICON_COMPONENTS[getThemeIconName(theme.name, {})] || Star;
                  return (
                    <button
                      key={`${theme.category_id}-${theme.name}`}
                      type="button"
                      onClick={() => {
                        startGame(theme.category_id, theme.name);
                      }}
                      className="flex min-h-[82px] flex-col items-center justify-center gap-1 rounded-2xl border border-white/10 bg-white/[0.03] px-2 py-3 text-center transition-colors hover:border-[#fccf1f]/40 hover:bg-[#fccf1f]/8"
                    >
                      <ThemeIcon className="h-5 w-5 text-[#fccf1f]" />
                      <span className="font-nunito text-[11px] font-bold leading-tight text-white">
                        {theme.name}
                      </span>
                    </button>
                  );
                })}
            </div>
            </div>
            {visibleCommunityQuizzes.length > 0 ? (
              <div className="rounded-[22px] border border-white/10 bg-zinc-950/75 p-4" data-catalog-palette="community">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h2 className="font-nunito text-lg font-black uppercase tracking-[0.08em] text-white">Quizzes da Comunidade</h2>
                  <span className="font-outfit text-[11px] uppercase tracking-[0.16em] text-zinc-500">{filteredCommunityQuizzes.length}</span>
                </div>
                <div className="space-y-2">
                  {visibleCommunityQuizzes.map((quiz) => (
                    <article key={quiz.id} className="rounded-2xl border border-white/10 bg-black/20 p-3.5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="truncate font-nunito text-base font-black text-white">{quiz.title}</h3>
                          <p className="mt-1 line-clamp-2 font-outfit text-xs leading-relaxed text-zinc-400">{quiz.description || "Um quiz criado pela comunidade LOTQ."}</p>
                          {quiz.creator_display_name || quiz.creator_name ? <p className="mt-2 font-outfit text-[11px] text-zinc-500">Por {quiz.creator_display_name || quiz.creator_name}</p> : null}
                        </div>
                        <Button type="button" size="sm" onClick={() => startCommunityQuiz(quiz.id)} className="shrink-0 bg-[#fccf1f] font-bold text-black hover:bg-[#e6bc1c]">Jogar</Button>
                      </div>
                    </article>
                  ))}
                </div>
                <InfiniteCatalogLoader
                  hasMore={visibleCommunityQuizzes.length < filteredCommunityQuizzes.length}
                  onLoadMore={() => setCommunityVisibleCount((count) => count + APP_CATALOG_PAGE_SIZE)}
                />
              </div>
            ) : null}
            {filteredCommunityQuizzes.length === 0 && filteredThemes.length === 0 ? (
              <div className="rounded-[22px] border border-white/10 bg-zinc-950/70 p-5 text-center text-sm text-zinc-400">Nenhum jogo encontrado.</div>
            ) : null}
          </section>
        ) : null}

        {appGamesPanel === "premium" ? (
          <section className="space-y-3" data-catalog-palette="premium">
            {visiblePremiumEvents.length > 0 ? (
              visiblePremiumEvents.map((event) => {
                const { needsUnlock, usesPass, canUsePass, paused, cost } = getPassAction(event);
                return (
                  <div key={event.id} className="rounded-[22px] border border-purple-500/25 bg-zinc-950/80 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-nunito text-lg font-black text-white">{event.title}</h3>
                        <p className="mt-1 font-outfit text-sm text-zinc-400">
                          {event.quiz_question_count || 10} perguntas • {event.quiz_time_per_question || 30}s por pergunta
                        </p>
                      </div>
                      <div className="rounded-xl border border-purple-500/30 bg-purple-500/10 p-2">
                        <Zap className="h-4 w-4 text-purple-300" />
                      </div>
                    </div>

                    {event.quiz_themes?.length ? (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {event.quiz_themes.map((theme) => (
                          <span key={`${event.id}-${theme}`} className="rounded-full border border-purple-500/20 bg-purple-500/10 px-2.5 py-1 text-[11px] text-purple-200">
                            {theme}
                          </span>
                        ))}
                      </div>
                    ) : null}

                    <div className="mt-4 flex gap-2">
                      {needsUnlock && (!canUsePass || paused) ? (
                        <Button
                          type="button"
                          onClick={() => openOnlineEventPurchase(event)}
                          disabled={paused}
                          className="flex-1 bg-[#fccf1f] text-black font-bold hover:bg-[#e6bc1c]"
                        >
                          {paused ? "Indisponível" : usesPass ? "Comprar passes" : "Comprar"}
                        </Button>
                      ) : (
                        <Button type="button" onClick={() => startPremiumGame(event.id)} className="w-full bg-purple-600 text-white font-bold hover:bg-purple-500">
                          {canUsePass ? `Usar ${cost} ${cost === 1 ? "passe" : "passes"}` : "Jogar"}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="rounded-[22px] border border-white/10 bg-zinc-950/70 p-5 text-center text-sm text-zinc-400">
                {searchTerms.premium ? "Nenhum quiz premium encontrado." : "Nenhum quiz premium ativo no momento."}
              </div>
            )}
            <InfiniteCatalogLoader
              hasMore={visiblePremiumEvents.length < filteredPremiumEvents.length}
              onLoadMore={() => setPremiumVisibleCount((count) => count + APP_CATALOG_PAGE_SIZE)}
            />
          </section>
        ) : null}

        {appGamesPanel === "special" ? (
          <section className="space-y-3" data-catalog-palette="special">
            {visibleSpecialEvents.length > 0 ? (
              visibleSpecialEvents.map((event) => {
                return (
                  <div key={event.id} className="rounded-[22px] border border-purple-500/25 bg-zinc-950/80 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-nunito text-lg font-black text-white">{event.title}</h3>
                        {event.description && <p className="mt-1 font-outfit text-sm text-zinc-400 whitespace-pre-line break-words">{event.description}</p>}
                        <p className="mt-1 font-outfit text-sm text-zinc-400">{event.quiz_question_count || 10} perguntas • {event.quiz_time_per_question || 30}s por pergunta</p>
                      </div>
                      <div className="rounded-xl border border-purple-500/30 bg-purple-500/10 p-2"><Zap className="h-4 w-4 text-purple-300" /></div>
                    </div>
                    <div className="mt-4 flex gap-2">
                      <div className="w-full"><SpecialGameActions event={event} onPlay={startSpecialGame} /></div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="rounded-[22px] border border-white/10 bg-zinc-950/70 p-5 text-center text-sm text-zinc-400">{searchTerms.special ? "Nenhum jogo especial encontrado." : "Nenhum jogo especial ativo no momento."}</div>
            )}
            <InfiniteCatalogLoader hasMore={specialGamesHasMore} onLoadMore={loadMoreSpecialGames} loading={specialGamesLoading} />
          </section>
        ) : null}

        {appGamesPanel === "presenciais" ? (
          <section className="space-y-3" data-catalog-palette="presencial">
            {visiblePresencialEvents.length > 0 ? (
              visiblePresencialEvents.map((event) => (
                <div key={event.id} className="rounded-[22px] border border-white/10 bg-zinc-950/80 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="font-nunito text-lg font-black text-white">{event.title}</h3>
                      <div className="mt-2 space-y-1 text-sm text-zinc-400">
                        <p>{formatDateBR(event.date)}</p>
                        {event.time ? <p>{event.time}</p> : null}
                        <p>{event.venue?.name || event.location}</p>
                        {(event.venue?.city || event.city) && <p>{event.venue?.city || event.city}</p>}
                      </div>
                    </div>
                    <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-2">
                      <MapPin className="h-4 w-4 text-emerald-300" />
                    </div>
                  </div>

                  <div className="mt-4 flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate(`/eventos/${event.slug || ""}`)}
                      className="flex-1 border-white/10 text-zinc-200 hover:bg-white/5"
                    >
                      Ver
                    </Button>
                    {eventNeedsTicket(event) ? (
                      <Button
                        type="button"
                        onClick={() => navigate(`/eventos?booking=${event.id}`)}
                        className="flex-1 bg-[#fccf1f] text-black font-bold hover:bg-[#e6bc1c]"
                      >
                        Comprar
                      </Button>
                    ) : null}
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-[22px] border border-white/10 bg-zinc-950/70 p-5 text-center text-sm text-zinc-400">
                {searchTerms.presenciais ? "Nenhum evento presencial encontrado." : "Nenhum evento presencial agendado no momento."}
              </div>
            )}
            <InfiniteCatalogLoader
              hasMore={visiblePresencialEvents.length < filteredPresencialEvents.length}
              onLoadMore={() => setPresencialVisibleCount((count) => count + APP_CATALOG_PAGE_SIZE)}
            />
          </section>
        ) : null}
      </div>
    </div>
  );
}

function GamesPageInner() {
  const { user, loading: authLoading, refreshUser } = useAuth();
  const { isApp } = usePlatform();
  const location = useLocation();
  const navigate = useNavigate();
  const { page: cmsPage } = useCMSPage("jogos");
  const [categories, setCategories] = useState([]);
  const [onlineEvents, setOnlineEvents] = useState([]);
  const [specialEvents, setSpecialEvents] = useState([]);
  const [specialGamesQuery, setSpecialGamesQuery] = useUrlSearchState("especiais", "");
  const [specialGamesHasMore, setSpecialGamesHasMore] = useState(false);
  const [specialGamesLoading, setSpecialGamesLoading] = useState(false);
  const [specialGamesTotal, setSpecialGamesTotal] = useState(0);
  const [presencialEvents, setPresencialEvents] = useState([]);
  const [seenPremiumEventIds, setSeenPremiumEventIds] = useState([]);
  const [seenPresencialEventIds, setSeenPresencialEventIds] = useState([]);
  const [communityQuizzes, setCommunityQuizzes] = useState([]);
  const [ownedOnlineEventIds, setOwnedOnlineEventIds] = useState({});
  const [gamePassWallet, setGamePassWallet] = useState({ balance: 0, unlimited: false, packs: [], entitled_event_ids: [] });
  const [showGamePassDialog, setShowGamePassDialog] = useState(false);
  const [gamePassReturnTo, setGamePassReturnTo] = useState("/jogos");
  const [buyingGamePassPackId, setBuyingGamePassPackId] = useState("");
  const [showAuth, setShowAuth] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [appGamesPanel, setAppGamesPanel] = useUrlSearchState("categoria", "online");

  const [gameSession, setGameSession] = useState(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [typedAnswer, setTypedAnswer] = useState("");
  const [imagePinSelection, setImagePinSelection] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [autoAdvanceSeconds, setAutoAdvanceSeconds] = useState(null);
  const [gameEnded, setGameEnded] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackComment, setFeedbackComment] = useState("");
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSaved, setFeedbackSaved] = useState(false);
  const [recommendedEvents, setRecommendedEvents] = useState([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [communityQuizLeaderboard, setCommunityQuizLeaderboard] = useState([]);
  const [communityQuizFeedback, setCommunityQuizFeedback] = useState(null);
  const [postGameMode, setPostGameMode] = useState(null);
  const [questionFeedbackState, setQuestionFeedbackState] = useState({});
  const [questionFeedbackSubmitting, setQuestionFeedbackSubmitting] = useState({});
  const [commentActionSubmitting, setCommentActionSubmitting] = useState({});
  const [reportSelections, setReportSelections] = useState({});
  const [reportComments, setReportComments] = useState({});
  const [openQuestionReports, setOpenQuestionReports] = useState({});
  const [preGame, setPreGame] = useState(null);
  const [preGameError, setPreGameError] = useState("");
  const [preGameBusy, setPreGameBusy] = useState(false);
  const preGameStarting = useRef(false);
  const checkoutBrowserListenerRef = useRef(null);
  const [timeLeftMs, setTimeLeftMs] = useState(DEFAULT_TIME_PER_QUESTION * 1000);
  const timerRef = useRef(null);
  const questionStartedAtRef = useRef(null);
  const answerDetailsRef = useRef([]);
  const handleAnswerRef = useRef(() => {});
  const goToNextQuestionRef = useRef(() => {});
  const protectedQuizRef = useRef(null);
  const activeSessionId = gameSession?.session_id || null;
  const isCommunityQuizSession = Boolean(gameSession?.community_quiz_id);

  useQuizContentProtection(protectedQuizRef, Boolean(gameSession));

  const scrollGameViewportToTop = useCallback(() => {
    requestAnimationFrame(() => {
      try {
        protectedQuizRef.current?.scrollIntoView({ block: "start", behavior: "auto" });
      } catch {}
      window.scrollTo({ top: 0, behavior: "auto" });
    });
  }, []);

  useEffect(() => {
    fetchCategories();
    fetchOnlineEvents();
    fetchPresencialEvents();
    fetchCommunityQuizzes();
  }, []);

  const loadSpecialGamesPage = useCallback(async ({ query = "", offset = 0, append = false } = {}) => {
    setSpecialGamesLoading(true);
    try {
      const res = await axios.get(`${API}/events/special-games/active`, {
        params: { search: query || undefined, offset, limit: SPECIAL_GAMES_PAGE_SIZE },
      });
      const payload = res.data || {};
      const items = Array.isArray(payload) ? payload : (payload.items || []);
      setSpecialEvents((current) => append ? [...current, ...items] : items);
      setSpecialGamesHasMore(Boolean(payload.has_more));
      setSpecialGamesTotal(Number(payload.total ?? items.length));
    } catch (err) {
      console.error("Error fetching special games:", err);
      if (!append) setSpecialEvents([]);
      setSpecialGamesHasMore(false);
      setSpecialGamesTotal(0);
    } finally {
      setSpecialGamesLoading(false);
    }
  }, []);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      loadSpecialGamesPage({ query: specialGamesQuery });
    }, specialGamesQuery ? 250 : 0);
    return () => window.clearTimeout(timeout);
  }, [loadSpecialGamesPage, specialGamesQuery]);

  useEffect(() => {
    try {
      const saved = JSON.parse(window.localStorage.getItem("lotq_seen_premium_events") || "[]");
      setSeenPremiumEventIds(Array.isArray(saved) ? saved : []);
    } catch {
      setSeenPremiumEventIds([]);
    }
  }, []);

  useEffect(() => {
    try {
      const saved = JSON.parse(window.localStorage.getItem("lotq_seen_presencial_events") || "[]");
      setSeenPresencialEventIds(Array.isArray(saved) ? saved : []);
    } catch {
      setSeenPresencialEventIds([]);
    }
  }, []);

  useEffect(() => {
    if (appGamesPanel !== "premium" || onlineEvents.length === 0) return;
    const currentIds = onlineEvents.map((event) => event.id).filter(Boolean);
    const mergedIds = Array.from(new Set([...seenPremiumEventIds, ...currentIds]));
    if (mergedIds.length === seenPremiumEventIds.length) return;
    setSeenPremiumEventIds(mergedIds);
    window.localStorage.setItem("lotq_seen_premium_events", JSON.stringify(mergedIds));
  }, [appGamesPanel, onlineEvents, seenPremiumEventIds]);

  useEffect(() => {
    if (appGamesPanel !== "presenciais" || presencialEvents.length === 0) return;
    const currentIds = presencialEvents.map((event) => event.id).filter(Boolean);
    const mergedIds = Array.from(new Set([...seenPresencialEventIds, ...currentIds]));
    if (mergedIds.length === seenPresencialEventIds.length) return;
    setSeenPresencialEventIds(mergedIds);
    window.localStorage.setItem("lotq_seen_presencial_events", JSON.stringify(mergedIds));
  }, [appGamesPanel, presencialEvents, seenPresencialEventIds]);

  const loadOwnedOnlineEventIds = useCallback(async () => {
    if (!user) {
      setOwnedOnlineEventIds({});
      return;
    }
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${API}/bookings/my-tickets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const ownedMap = {};
      (response.data || []).forEach((booking) => {
        if (booking?.event?.event_type === "online" && booking?.event_id) {
          ownedMap[booking.event_id] = true;
        }
      });
      setOwnedOnlineEventIds(ownedMap);
    } catch (error) {
      console.error("Error loading owned online tickets on games page:", error);
      setOwnedOnlineEventIds({});
    }
  }, [user]);

  const loadGamePassWallet = useCallback(async () => {
    if (!user) {
      setGamePassWallet({ balance: 0, unlimited: false, packs: [], entitled_event_ids: [] });
      return;
    }
    try {
      const token = localStorage.getItem("auth_token");
      const headers = { Authorization: `Bearer ${token}` };
      const walletResponse = await axios.get(`${API}/game-passes/me`, { headers });
      // Keep the purchase catalog independent from the authenticated balance.
      // If the catalog is temporarily unavailable, the wallet remains usable.
      const catalogResponse = await axios.get(`${API}/game-passes/catalog`).catch(() => null);
      setGamePassWallet({
        ...(walletResponse.data || {}),
        packs: catalogResponse?.data || walletResponse.data?.packs || [],
      });
    } catch (error) {
      console.error("Error loading game-pass wallet:", error);
    }
  }, [user]);

  useEffect(() => {
    loadOwnedOnlineEventIds();
  }, [loadOwnedOnlineEventIds]);

  useEffect(() => {
    loadGamePassWallet();
  }, [loadGamePassWallet]);

  useEffect(() => {
    if (!user) return undefined;

    const refreshWalletOnReturn = () => {
      if (document.visibilityState === "visible") loadGamePassWallet();
    };

    window.addEventListener("focus", refreshWalletOnReturn);
    document.addEventListener("visibilitychange", refreshWalletOnReturn);
    return () => {
      window.removeEventListener("focus", refreshWalletOnReturn);
      document.removeEventListener("visibilitychange", refreshWalletOnReturn);
    };
  }, [loadGamePassWallet, user]);

  useEffect(() => () => {
    checkoutBrowserListenerRef.current?.remove?.();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("buyPasses") !== "1") return;
    setGamePassReturnTo("/jogos");
    setShowGamePassDialog(true);
    navigate("/jogos", { replace: true });
  }, [location.search, navigate]);

  useEffect(() => {
    if (!location.state?.preloadedGameSession) return;
    scrollGameViewportToTop();
    setGameSession(location.state.preloadedGameSession);
    setCurrentIdx(0);
    setScore(0);
    setSelectedAnswer(null);
    setImagePinSelection(null);
    setShowResult(false);
    setGameEnded(false);
    setGameStarted(!location.state.preloadedGameSession?.community_quiz_id);
    setCommunityQuizLeaderboard([]);
    answerDetailsRef.current = [];
    setTimeLeftMs((location.state.preloadedGameSession?.time_per_question || DEFAULT_TIME_PER_QUESTION) * 1000);
  }, [location.state, scrollGameViewportToTop]);

  useEffect(() => {
    if (!activeSessionId || gameEnded) return;
    scrollGameViewportToTop();
  }, [activeSessionId, gameEnded, gameStarted, isCommunityQuizSession, scrollGameViewportToTop]);

  useEffect(() => {
    if (!gameSession || gameEnded) return;
    if (isCommunityQuizSession && !gameStarted) return;
    scrollGameViewportToTop();
  }, [currentIdx, gameEnded, gameSession, gameStarted, isCommunityQuizSession, scrollGameViewportToTop]);

  const questionTimeLimitMs = (gameSession?.time_per_question || DEFAULT_TIME_PER_QUESTION) * 1000;
  const isAdmin = isAdminUser(user);

  useEffect(() => {
    if (!gameSession || showResult || gameEnded || !gameStarted) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    questionStartedAtRef.current = Date.now();
    setTimeLeftMs(questionTimeLimitMs);

    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - (questionStartedAtRef.current || Date.now());
      const remaining = Math.max(0, questionTimeLimitMs - elapsed);
      setTimeLeftMs(remaining);
      if (remaining <= 0) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        handleAnswerRef.current(-1);
      }
    }, 50);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [currentIdx, gameEnded, gameSession, gameStarted, questionTimeLimitMs, showResult]);


  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/quiz/categories-themes`);
      setCategories(res.data || []);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  const fetchOnlineEvents = async () => {
    try {
      const res = await axios.get(`${API}/events/online/active`);
      setOnlineEvents(res.data || []);
    } catch (err) {
      console.error("Error fetching online events:", err);
    }
  };

  const loadMoreSpecialGames = useCallback(() => {
    if (specialGamesLoading || !specialGamesHasMore) return;
    loadSpecialGamesPage({
      query: specialGamesQuery,
      offset: specialEvents.length,
      append: true,
    });
  }, [loadSpecialGamesPage, specialEvents.length, specialGamesHasMore, specialGamesLoading, specialGamesQuery]);

  const fetchPresencialEvents = async () => {
    try {
      const res = await axios.get(`${API}/events?limit=100`);
      const presenciais = (res.data || []).filter((event) => !isOnlineGameEvent(event) && !isEventCancelled(event));
      const upcoming = presenciais
        .filter((event) => !event.is_past)
        .sort((a, b) => (a.date || "").localeCompare(b.date || ""));

      setPresencialEvents(upcoming);
    } catch (err) {
      console.error("Error fetching presencial events:", err);
    }
  };

  const fetchCommunityQuizzes = async () => {
    try {
      const res = await axios.get(`${API}/quiz/community-quizzes/public`);
      setCommunityQuizzes(res.data?.quizzes || []);
    } catch (err) {
      console.error("Error fetching community quizzes:", err);
    }
  };

  const eventNeedsTicket = useCallback((event) => {
    if (!event) return false;
    return Boolean(
      event.requires_game_pass
      ||
      event.requires_ticket
      || (event.selected_ticket_type_ids || []).length
      || (event.ticket_tiers || []).length
    );
  }, []);

  const eventUsesGamePass = useCallback((event) => (
    Boolean(event?.requires_game_pass)
    || (
      event?.event_type === "online"
      && ["special", "premium"].includes(event?.catalog_section)
      && eventNeedsTicket(event)
    )
  ), [eventNeedsTicket]);

  const userCanAccessOnlineEvent = useCallback((event) => {
    if (!event || event.event_type !== "online") return false;
    if (!eventNeedsTicket(event)) return true;
    if (!user) return false;
    const userPlan = user.subscription_plan || "free";
    if ((event.free_plans || []).includes(userPlan)) return true;
    if (eventUsesGamePass(event) && gamePassWallet.unlimited) return true;
    if ((gamePassWallet.entitled_event_ids || []).includes(event.id)) return true;
    return Boolean(ownedOnlineEventIds[event.id]);
  }, [eventNeedsTicket, eventUsesGamePass, gamePassWallet.entitled_event_ids, gamePassWallet.unlimited, ownedOnlineEventIds, user]);

  const getGamePassCost = useCallback((event) => Math.max(1, Number.parseInt(event?.game_pass_cost, 10) || 1), []);

  const hasAvailableGamePassForEvent = useCallback((event) => (
    eventUsesGamePass(event)
    && !event.ticket_sales_paused
    && (gamePassWallet.unlimited || Number(gamePassWallet.balance || 0) >= getGamePassCost(event))
  ), [eventUsesGamePass, gamePassWallet.balance, gamePassWallet.unlimited, getGamePassCost]);

  const getOnlineEventPath = useCallback((event) => (
    event?.catalog_section === "special"
      ? `/jogos?specialGame=${encodeURIComponent(event.id)}`
      : event?.is_streaming && event?.streaming_mode === "embedded_room"
      ? `/jogos?premiumGame=${encodeURIComponent(event.id)}`
      : `/quiz-especial/${event.id}`
  ), []);

  const openOnlineEventPurchase = useCallback((event) => {
    if (!user) {
      setShowAuth(true);
      return;
    }
    if (event.ticket_sales_paused && !userCanAccessOnlineEvent(event)) {
      toast.info("Este jogo está temporariamente indisponível para novos desbloqueios.");
      return;
    }
    if (eventUsesGamePass(event)) {
      setGamePassReturnTo(getOnlineEventPath(event));
      setShowGamePassDialog(true);
      return;
    }
    navigate(`/eventos?booking=${event.id}&return_to=${encodeURIComponent(getOnlineEventPath(event))}`);
  }, [eventUsesGamePass, getOnlineEventPath, navigate, user, userCanAccessOnlineEvent]);

  const openGamePassCheckout = useCallback(async (url, orderId, returnTo) => {
    if (!isApp || !Capacitor.isNativePlatform()) {
      window.location.assign(url);
      return;
    }

    // Checkout stays isolated in a Custom Tab. Closing it always returns to
    // the app, where the order status is verified before the balance refreshes.
    checkoutBrowserListenerRef.current?.remove?.();
    const successPath = `/checkout/success?game_pass_order_id=${encodeURIComponent(orderId)}&provider=infinitepay&native_return=1&return_to=${encodeURIComponent(returnTo)}`;
    checkoutBrowserListenerRef.current = await Browser.addListener("browserFinished", () => {
      checkoutBrowserListenerRef.current?.remove?.();
      checkoutBrowserListenerRef.current = null;
      navigate(successPath);
    });

    try {
      await Browser.open({ url });
    } catch (error) {
      checkoutBrowserListenerRef.current?.remove?.();
      checkoutBrowserListenerRef.current = null;
      throw error;
    }
  }, [isApp, navigate]);

  const buyGamePassPack = useCallback(async (packId, returnTo = "/jogos") => {
    if (!user) {
      setShowGamePassDialog(false);
      setShowAuth(true);
      return;
    }
    setBuyingGamePassPackId(packId);
    try {
      const selectedPack = (gamePassWallet.packs || []).find((pack) => pack.id === packId);
      trackMetaEvent(
        "InitiateCheckout",
        {
          content_ids: [`game-pass-pack:${packId}`],
          content_name: selectedPack?.name || "Passes de Jogo",
          content_category: "Passes de Jogo",
          content_type: "product",
          currency: "BRL",
          value: Number(selectedPack?.price || 0),
          num_items: Math.max(1, Number(selectedPack?.passes || 1)),
        },
        `game-pass-start:${packId}:${Date.now()}`,
      );
      const token = localStorage.getItem("auth_token");
      const headers = { Authorization: `Bearer ${token}` };
      const order = await axios.post(`${API}/game-passes/orders`, { pack_id: packId }, { headers });
      const checkout = await axios.post(
        `${API}/game-passes/orders/${order.data.id}/checkout`,
        { origin_url: window.location.origin, return_to: returnTo },
        { headers },
      );
      if (checkout.data?.status === "confirmed") {
        await Promise.all([loadGamePassWallet(), refreshUser()]);
        setShowGamePassDialog(false);
        return;
      }
      if (!checkout.data?.url) throw new Error("Checkout sem URL");
      await openGamePassCheckout(checkout.data.url, order.data.id, returnTo);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Não foi possível iniciar a compra de Passes de Jogo.");
    } finally {
      setBuyingGamePassPackId("");
    }
  }, [gamePassWallet.packs, loadGamePassWallet, openGamePassCheckout, refreshUser, user]);


  const gamesGridSection = useMemo(
    () => cmsPage?.sections?.find((section) => section.type === "games_list" && section.visible !== false) || null,
    [cmsPage]
  );

  const gamesGridConfig = useMemo(
    () => normalizeGamesGridConfig(gamesGridSection?.content || {}),
    [gamesGridSection]
  );

  const themeGridClasses = useMemo(() => {
    const mobile = MOBILE_GRID_CLASS[gamesGridConfig.mobileColumns] || MOBILE_GRID_CLASS[2];
    const tablet = TABLET_GRID_CLASS[gamesGridConfig.tabletColumns] || TABLET_GRID_CLASS[4];
    const desktop = DESKTOP_GRID_CLASS[gamesGridConfig.desktopColumns] || DESKTOP_GRID_CLASS[8];
    return `${mobile} ${tablet} ${desktop}`;
  }, [gamesGridConfig.desktopColumns, gamesGridConfig.mobileColumns, gamesGridConfig.tabletColumns]);

  const allThemes = useMemo(() => {
    const dedupedThemes = new Map();

    categories.forEach((category) => {
      (category.themes || []).forEach((theme) => {
        const normalizedName = normalizeComparableText(theme.name);
        if (!normalizedName) return;

        const nextTheme = {
          ...theme,
          category_id: category.id,
          category_name: category.name,
        };

        const currentTheme = dedupedThemes.get(normalizedName);
        if (!currentTheme) {
          dedupedThemes.set(normalizedName, nextTheme);
          return;
        }

        const currentAvailable = Boolean(currentTheme.available);
        const nextAvailable = Boolean(nextTheme.available);
        const currentQuestions = Number(currentTheme.question_count || 0);
        const nextQuestions = Number(nextTheme.question_count || 0);

        if ((!currentAvailable && nextAvailable) || (currentAvailable === nextAvailable && nextQuestions > currentQuestions)) {
          dedupedThemes.set(normalizedName, nextTheme);
        }
      });
    });

    const flattened = Array.from(dedupedThemes.values()).sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
    return gamesGridConfig.maxItems ? flattened.slice(0, gamesGridConfig.maxItems) : flattened;
  }, [categories, gamesGridConfig.maxItems]);

  const orderedGameSections = useMemo(() => {
    const fallback = ["online", "special", "premium", "community", "presencial"];
    const configured = gamesGridConfig.sectionOrder?.length ? gamesGridConfig.sectionOrder : fallback;
    const deduped = [...new Set(configured.filter((item) => fallback.includes(item)))];
    return [...deduped, ...fallback.filter((item) => !deduped.includes(item))];
  }, [gamesGridConfig.sectionOrder]);

  const gamesPlayedToday = user?.games_played_today ?? 0;
  const gamesLimitDay = user?.games_limit_day ?? 1;
  const isUnlimitedDay = user?.games_unlimited_day || gamesLimitDay === -1;
  const gamesRemainingToday = isUnlimitedDay ? -1 : Math.max(gamesLimitDay - gamesPlayedToday, 0);
  const premiumNewCount = onlineEvents.filter((event) => event?.id && !seenPremiumEventIds.includes(event.id)).length;
  const unseenPresencialCount = presencialEvents.filter((event) => !seenPresencialEventIds.includes(event.id)).length;

  const beginGame = async (categoryId, theme) => {
    if (!user) {
      setShowAuth(true);
      return;
    }

    scrollGameViewportToTop();
    setLoading(true);
    const token = localStorage.getItem("auth_token");

    try {
      const res = await axios.post(
        `${API}/quiz/start-game`,
        { category_id: categoryId, theme },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGameSession(res.data);
      setCurrentIdx(0);
      setScore(0);
      setSelectedAnswer(null);
      setTypedAnswer("");
      setImagePinSelection(null);
      setShowResult(false);
      setGameEnded(false);
      setGameStarted(true);
      answerDetailsRef.current = [];
      setTimeLeftMs((res.data?.time_per_question || DEFAULT_TIME_PER_QUESTION) * 1000);
      scrollGameViewportToTop();
      return true;
    } catch (err) {
      const msg = err.response?.data?.detail || "Erro ao iniciar jogo";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const beginCommunityQuiz = useCallback(async (quizId) => {
    scrollGameViewportToTop();
    setLoading(true);
    const token = localStorage.getItem("auth_token");
    try {
      const res = await axios.post(
        `${API}/quiz/community-quizzes/${quizId}/play`,
        {},
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
      );
      setGameSession(res.data);
      setCurrentIdx(0);
      setScore(0);
      setSelectedAnswer(null);
      setTypedAnswer("");
      setImagePinSelection(null);
      setShowResult(false);
      setGameEnded(false);
      setGameStarted(true);
      answerDetailsRef.current = [];
      setTimeLeftMs((res.data?.time_per_question || DEFAULT_TIME_PER_QUESTION) * 1000);
      scrollGameViewportToTop();
      return true;
    } catch (err) {
      const msg = err.response?.data?.detail || "Erro ao iniciar quiz da comunidade";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [scrollGameViewportToTop]);

  const beginSpecialGame = useCallback(async (eventId) => {
    if (!user) {
      setShowAuth(true);
      return;
    }
    scrollGameViewportToTop();
    setLoading(true);
    const token = localStorage.getItem("auth_token");
    try {
      const res = await axios.post(`${API}/quiz/special-games/${eventId}/play`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setGameSession(res.data);
      setCurrentIdx(0);
      setScore(0);
      setSelectedAnswer(null);
      setTypedAnswer("");
      setImagePinSelection(null);
      setShowResult(false);
      setGameEnded(false);
      setGameStarted(true);
      answerDetailsRef.current = [];
      setTimeLeftMs((res.data?.time_per_question || DEFAULT_TIME_PER_QUESTION) * 1000);
      scrollGameViewportToTop();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao iniciar Jogo Especial");
    } finally {
      setLoading(false);
    }
  }, [scrollGameViewportToTop, user]);

  const coverParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    for (const [key, kind] of [["specialGame", "special"], ["communityQuiz", "community"], ["premiumGame", "premium"], ["onlineGame", "online"]]) {
      if (params.get(key)) return { kind, id: params.get(key), theme: params.get("theme") || "" };
    }
    return null;
  }, [location.search]);

  useEffect(() => {
    let current = true;
    setPreGame(null);
    setPreGameError("");
    if (!coverParams) return;
    axios.get(`${API}/quiz/pre-game/${coverParams.kind}/${encodeURIComponent(coverParams.id)}`, { params: { theme: coverParams.theme || undefined } })
      .then(({ data }) => { if (current) setPreGame(data); })
      .catch((err) => { if (current) setPreGameError(err.response?.data?.detail || "Não foi possível carregar este jogo."); });
    return () => { current = false; };
  }, [coverParams]);

  const startGame = (categoryId, theme) => navigate(`/jogos?onlineGame=${encodeURIComponent(categoryId)}&theme=${encodeURIComponent(theme)}`);
  const startCommunityQuiz = (quizId) => navigate(`/jogos?communityQuiz=${encodeURIComponent(quizId)}`);
  const startPremiumGame = (eventId) => navigate(`/jogos?premiumGame=${encodeURIComponent(eventId)}`);
  const startSpecialGame = (eventId) => navigate(`/jogos?specialGame=${encodeURIComponent(eventId)}`);
  const closeCover = () => {
    if (gameEnded) { setGameSession(null); setGameEnded(false); setGameStarted(false); }
    navigate("/jogos", { replace: true });
  };
  const confirmPreGame = async () => {
    if (!preGame || preGameStarting.current) return;
    if (!user && preGame.kind !== "community") { setShowAuth(true); return; }
    if (preGame.ticket_sales_paused && !userCanAccessOnlineEvent(preGame)) {
      toast.info("Este jogo está temporariamente indisponível para novos desbloqueios.");
      return;
    }
    const gamePassCost = getGamePassCost(preGame);
    if (preGame.requires_game_pass && !userCanAccessOnlineEvent(preGame) && Number(gamePassWallet.balance || 0) < gamePassCost) {
      setGamePassReturnTo(getOnlineEventPath(preGame));
      setShowGamePassDialog(true);
      return;
    }
    if (preGame.kind === "premium") {
      if (preGame.requires_game_pass && !userCanAccessOnlineEvent(preGame)) {
        setPreGameBusy(true);
        try {
          const token = localStorage.getItem("auth_token");
          const claim = await axios.post(`${API}/game-passes/claim/${preGame.id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
          if (!claim.data?.has_access) {
            toast.error(claim.data?.message || "Você precisa de um Passe de Jogo para continuar.");
            return;
          }
          await Promise.all([loadGamePassWallet(), refreshUser()]);
        } catch (error) {
          toast.error(error.response?.data?.detail || "Não foi possível desbloquear este jogo.");
          return;
        } finally {
          setPreGameBusy(false);
        }
      }
      navigate(preGame.is_streaming && preGame.streaming_mode === "embedded_room" ? `/sala-evento/${preGame.id}` : `/quiz-especial/${preGame.id}`);
      return;
    }
    if (preGame.kind === "special" && !userCanAccessOnlineEvent(preGame) && !preGame.requires_game_pass) {
      openOnlineEventPurchase(preGame); return;
    }
    preGameStarting.current = true;
    setPreGameBusy(true);
    try {
      const started = preGame.kind === "special" ? await beginSpecialGame(preGame.id)
        : preGame.kind === "community" ? await beginCommunityQuiz(preGame.id)
        : await beginGame(preGame.id, preGame.theme);
      if (started) navigate("/jogos", { replace: true });
    } finally { preGameStarting.current = false; setPreGameBusy(false); }
  };
  const preGameCover = <PreGameCover open={Boolean(coverParams) && (!gameSession || gameEnded) && !showAuth && !showGamePassDialog} game={preGame} busy={preGameBusy} error={preGameError} onClose={closeCover} onStart={confirmPreGame}
    actionLabel={!user && preGame?.kind !== "community" ? "Entrar para jogar" : preGame?.ticket_sales_paused && !userCanAccessOnlineEvent(preGame) ? "Indisponível" : preGame?.requires_game_pass && !userCanAccessOnlineEvent(preGame) ? (Number(gamePassWallet.balance || 0) >= getGamePassCost(preGame) ? `Usar ${getGamePassCost(preGame)} ${getGamePassCost(preGame) === 1 ? "passe" : "passes"}` : "Comprar passes") : preGame?.kind === "premium" ? "Entrar na sala" : preGame?.requires_ticket && !userCanAccessOnlineEvent(preGame) ? "Comprar ingresso" : "Começar quiz"}
    shareUrl={new URL(`/jogos${location.search}`, process.env.REACT_APP_BACKEND_URL || window.location.origin).href}>
    {preGame?.requires_game_pass && <div className="rounded-xl border border-[#fccf1f]/30 bg-[#fccf1f]/5 p-3 text-sm">
      {userCanAccessOnlineEvent(preGame) ? "Seu acesso a este jogo já está liberado." : !user ? "Entre na sua conta para consultar seus passes e desbloquear este jogo." : <>
        <p>Este jogo custa {getGamePassCost(preGame)} {getGamePassCost(preGame) === 1 ? "passe" : "passes"}. Seu saldo: {Number(gamePassWallet.balance || 0)}.</p>
        <p className="mt-1">{Number(gamePassWallet.balance || 0) >= getGamePassCost(preGame)
          ? "Você tem saldo suficiente. Confirme abaixo para usar seus passes e liberar o jogo."
          : "Seu saldo não é suficiente. Compre um pacote de passes para liberar este jogo."}</p>
      </>}
    </div>}
  </PreGameCover>;

  const handleImagePinSelect = (event) => {
    if (showResult || !question?.media_url) return;
    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const nextPoint = normalizeImagePinPoint({
      x: (event.clientX - rect.left) / rect.width,
      y: (event.clientY - rect.top) / rect.height,
    });
    if (!nextPoint) return;
    setImagePinSelection(nextPoint);
  };

  const handleAnswer = (answerIdx, submittedText = "", submittedPoint = null) => {
    if (showResult || !gameSession) return;

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setSelectedAnswer(answerIdx);
    setTypedAnswer(submittedText);
    setImagePinSelection(submittedPoint);
    setShowResult(true);

    const currentQuestion = gameSession.questions[currentIdx];
    const normalizedSubmittedText = normalizeComparableText(submittedText);
    const isShortAnswer = currentQuestion.question_type === QUESTION_TYPE_SHORT_ANSWER;
    const isNumericRange = currentQuestion.question_type === QUESTION_TYPE_NUMERIC_RANGE;
    const isImagePin = currentQuestion.question_type === QUESTION_TYPE_IMAGE_PIN;
    const correct = currentQuestion.correct_answer;
    const acceptedShortAnswers = getAcceptedShortAnswers(currentQuestion).map((option) => normalizeComparableText(option));
    const submittedNumericValue = parseNumericAnswer(submittedText);
    const numericMin = parseNumericAnswer(currentQuestion.numeric_min);
    const numericMax = parseNumericAnswer(currentQuestion.numeric_max);
    const normalizedSubmittedPoint = normalizeImagePinPoint(submittedPoint);
    const correctImagePinPoints = normalizeImagePinPoints(currentQuestion.image_pin_points);
    const resolvedIsCorrect = isShortAnswer
      ? acceptedShortAnswers.some((expected) => normalizedSubmittedText && (normalizedSubmittedText === expected || isLenientShortAnswerMatch(normalizedSubmittedText, expected)))
      : isNumericRange
        ? submittedNumericValue !== null && numericExpectedValue !== null && submittedNumericValue === numericExpectedValue
        : isImagePin
          ? isPointInsidePolygon(normalizedSubmittedPoint, correctImagePinPoints)
          : answerIdx === correct;
    const elapsedMs = Math.max(0, Date.now() - (questionStartedAtRef.current || Date.now()));
    const awardedPoints = calculateQuestionPoints(resolvedIsCorrect, elapsedMs, questionTimeLimitMs);
    const newScore = score + awardedPoints;

    answerDetailsRef.current = [
      ...answerDetailsRef.current.filter((entry) => entry.question_id !== currentQuestion.id),
      {
        question_id: currentQuestion.id,
        selected_answer: answerIdx,
        selected_text: submittedText,
        selected_point: normalizedSubmittedPoint,
        time_taken_ms: Math.min(elapsedMs, questionTimeLimitMs),
      },
    ];

    if (awardedPoints > 0) {
      setScore(newScore);
    }

  };

  handleAnswerRef.current = handleAnswer;

  const goToNextQuestion = () => {
    if (!gameSession) return;
    if (currentIdx < gameSession.questions.length - 1) {
      setCurrentIdx(currentIdx + 1);
      setSelectedAnswer(null);
      setTypedAnswer("");
      setImagePinSelection(null);
      setShowResult(false);
      return;
    }
    finishGame(score);
  };

  goToNextQuestionRef.current = goToNextQuestion;

  useEffect(() => {
    if (!showResult || !activeSessionId || gameEnded) {
      setAutoAdvanceSeconds(null);
      return undefined;
    }

    const advanceAt = Date.now() + 3000;
    setAutoAdvanceSeconds(3);
    const countdownId = window.setInterval(() => {
      setAutoAdvanceSeconds(Math.max(0, Math.ceil((advanceAt - Date.now()) / 1000)));
    }, 100);
    const autoAdvanceId = window.setTimeout(() => {
      goToNextQuestionRef.current();
    }, 3000);

    return () => {
      window.clearInterval(countdownId);
      window.clearTimeout(autoAdvanceId);
    };
  }, [currentIdx, gameEnded, activeSessionId, showResult]);

  const finishGame = async (finalScore) => {
    setGameEnded(true);
    setSubmitting(true);
    setFeedbackSaved(false);
    setFeedbackRating(0);
    setFeedbackComment("");
    setPostGameMode(null);
    setQuestionFeedbackState({});
    setQuestionFeedbackSubmitting({});
    setReportSelections({});
    setReportComments({});
    setCommunityQuizLeaderboard([]);
    setCommunityQuizFeedback(null);
    const token = localStorage.getItem("auth_token");

    try {
      const { data } = await axios.post(
        `${API}/quiz/submit-game`,
        {
          session_id: gameSession.session_id,
          total_questions: gameSession.questions.length,
          answer_details: answerDetailsRef.current,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setScore(data?.score ?? finalScore);
      setCommunityQuizLeaderboard(data?.community_quiz_leaderboard || []);
      setCommunityQuizFeedback(data?.community_quiz_feedback || null);
      refreshUser();
    } catch (err) {
      console.error("Error submitting game:", err);
    } finally {
      setSubmitting(false);
    }

    setLoadingRecommendations(true);
    try {
      const { data } = await axios.get(`${API}/quiz/recommended-events`, {
        params: { session_id: gameSession.session_id },
      });
      setRecommendedEvents(data?.events || []);
    } catch (err) {
      console.error("Error fetching recommended events:", err);
      setRecommendedEvents([]);
    } finally {
      setLoadingRecommendations(false);
    }
  };

  const submitOverallFeedback = async () => {
    if (!gameSession?.session_id) return;
    if (!feedbackRating) {
      toast.error("Escolha uma nota para o quiz antes de enviar.");
      return;
    }

    setFeedbackSubmitting(true);
    const token = localStorage.getItem("auth_token");
    try {
      const { data } = await axios.post(
        `${API}/quiz/submit-feedback`,
        {
          session_id: gameSession.session_id,
          rating: feedbackRating,
          comment: feedbackComment.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFeedbackSaved(true);
      if (data?.community_quiz_feedback) {
        setCommunityQuizFeedback(data.community_quiz_feedback);
      }
      toast.success("Obrigado pelo feedback!");
    } catch (err) {
      console.error("Error submitting quiz feedback:", err);
      toast.error(err.response?.data?.detail || "Não foi possível enviar seu feedback.");
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const submitQuestionFeedback = async ({ questionId, feedbackType, errorType = null, comment = "" }) => {
    if (!gameSession?.session_id || !questionId) return;

    const submitKey = `${questionId}:${feedbackType}`;
    setQuestionFeedbackSubmitting((current) => ({ ...current, [submitKey]: true }));
    const token = localStorage.getItem("auth_token");

    try {
      await axios.post(
        `${API}/quiz/question-feedback`,
        {
          session_id: gameSession.session_id,
          question_id: questionId,
          feedback_type: feedbackType,
          error_type: errorType,
          comment: comment.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setQuestionFeedbackState((current) => ({
        ...current,
        [submitKey]: {
          success: true,
          errorType,
          comment: comment.trim(),
          updatedAt: Date.now(),
        },
      }));

      toast.success(
        feedbackType === "report_error"
          ? "Obrigado por reportar esse possível problema."
          : "Feedback da pergunta salvo."
      );
    } catch (err) {
      console.error("Error submitting question feedback:", err);
      toast.error(err.response?.data?.detail || "Não foi possível salvar esse feedback.");
    } finally {
      setQuestionFeedbackSubmitting((current) => ({ ...current, [submitKey]: false }));
    }
  };

  const reportCommunityComment = async (commentId) => {
    if (!user) {
      setShowAuth(true);
      return;
    }
    if (!window.confirm("Deseja reportar este comentário para moderação?")) return;

    const submitKey = `report-comment:${commentId}`;
    setCommentActionSubmitting((current) => ({ ...current, [submitKey]: true }));
    const token = localStorage.getItem("auth_token");

    try {
      await axios.post(
        `${API}/quiz/community-comments/${commentId}/report`,
        { reason: "abusive" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Comentário reportado para moderação.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível reportar este comentário.");
    } finally {
      setCommentActionSubmitting((current) => ({ ...current, [submitKey]: false }));
    }
  };

  const deleteCommunityComment = async (commentId) => {
    if (!isAdmin) return;
    if (!window.confirm("Tem certeza que deseja excluir este comentário?")) return;

    const submitKey = `delete-comment:${commentId}`;
    setCommentActionSubmitting((current) => ({ ...current, [submitKey]: true }));
    const token = localStorage.getItem("auth_token");

    try {
      const { data } = await axios.delete(`${API}/quiz/community-comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (data?.community_quiz_feedback) {
        setCommunityQuizFeedback(data.community_quiz_feedback);
      }
      await fetchCommunityQuizzes();
      toast.success("Comentário excluído.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível excluir este comentário.");
    } finally {
      setCommentActionSubmitting((current) => ({ ...current, [submitKey]: false }));
    }
  };

  const resetGame = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setGameSession(null);
    setCurrentIdx(0);
    setScore(0);
    setSelectedAnswer(null);
    setTypedAnswer("");
    setImagePinSelection(null);
    setShowResult(false);
    setGameEnded(false);
    setGameStarted(false);
    setShowLeaderboard(false);
    setFeedbackRating(0);
    setFeedbackComment("");
    setFeedbackSaved(false);
    setRecommendedEvents([]);
    setLoadingRecommendations(false);
    setCommunityQuizLeaderboard([]);
    setCommunityQuizFeedback(null);
    setPostGameMode(null);
    setQuestionFeedbackState({});
    setQuestionFeedbackSubmitting({});
    setReportSelections({});
    setReportComments({});
    setTimeLeftMs(DEFAULT_TIME_PER_QUESTION * 1000);
    answerDetailsRef.current = [];
  };

  const question = gameSession?.questions?.[currentIdx];
  const isTrueFalseQuestion = question?.question_type === QUESTION_TYPE_TRUE_FALSE;
  const isShortAnswerQuestion = question?.question_type === QUESTION_TYPE_SHORT_ANSWER;
  const isNumericRangeQuestion = question?.question_type === QUESTION_TYPE_NUMERIC_RANGE;
  const isImagePinQuestion = question?.question_type === QUESTION_TYPE_IMAGE_PIN;
  const maxPossibleScore = (gameSession?.questions?.length || 0) * 1000;
  const scorePct = maxPossibleScore > 0 ? Math.round((score / maxPossibleScore) * 100) : 0;
  const timeProgressPct = questionTimeLimitMs > 0 ? Math.max(0, Math.min(100, (timeLeftMs / questionTimeLimitMs) * 100)) : 0;
  const acceptedShortAnswers = getAcceptedShortAnswers(question);
  const normalizedSubmittedAnswer = normalizeComparableText(typedAnswer);
  const isShortAnswerCorrect = acceptedShortAnswers.some((option) => {
    const normalizedExpected = normalizeComparableText(option);
    return normalizedSubmittedAnswer && (normalizedSubmittedAnswer === normalizedExpected || isLenientShortAnswerMatch(normalizedSubmittedAnswer, normalizedExpected));
  });
  const submittedNumericAnswer = parseNumericAnswer(typedAnswer);
  const numericMinValue = parseNumericAnswer(question?.numeric_min);
  const numericMaxValue = parseNumericAnswer(question?.numeric_max);
  const numericExpectedValue = parseNumericAnswer(question?.answer_text);
  const numericSliderStep = getNumericSliderStep(question?.numeric_min, question?.numeric_max, question?.answer_text);
  const numericRangeMidpoint = numericMinValue !== null && numericMaxValue !== null
    ? alignNumericValueToStep((numericMinValue + numericMaxValue) / 2, numericMinValue, numericSliderStep)
    : null;
  const numericSliderValue = submittedNumericAnswer !== null
    ? submittedNumericAnswer
    : (numericRangeMidpoint !== null ? numericRangeMidpoint : numericMinValue);
  const numericSliderPercent = numericMinValue !== null
    && numericMaxValue !== null
    && numericMaxValue > numericMinValue
    && numericSliderValue !== null
    ? ((numericSliderValue - numericMinValue) / (numericMaxValue - numericMinValue)) * 100
    : 0;
  const mediaStartSeconds = Number.isFinite(Number(question?.media_start_seconds)) ? Number(question.media_start_seconds) : null;
  const mediaEndSeconds = Number.isFinite(Number(question?.media_end_seconds)) ? Number(question.media_end_seconds) : null;
  const timedQuestionDirectMediaUrl = buildDirectMediaUrl(question?.media_url, mediaStartSeconds, mediaEndSeconds);
  const timedQuestionYouTubeUrl = getTimedYouTubeEmbedUrl(question?.media_url, mediaStartSeconds, mediaEndSeconds);
  const timedQuestionVimeoUrl = getTimedVimeoEmbedUrl(question?.media_url, mediaStartSeconds);
  const isNumericRangeCorrect = submittedNumericAnswer !== null
    && numericExpectedValue !== null
    && submittedNumericAnswer === numericExpectedValue;
  const imagePinPolygon = normalizeImagePinPoints(question?.image_pin_points);
  const imagePinSelectionPercent = imagePinSelection
    ? {
      left: `${imagePinSelection.x * 100}%`,
      top: `${imagePinSelection.y * 100}%`,
    }
    : null;
  const isImagePinCorrect = isImagePinQuestion
    ? isPointInsidePolygon(imagePinSelection, imagePinPolygon)
    : false;
  const answerDetailsMap = new Map((answerDetailsRef.current || []).map((entry) => [entry.question_id, entry]));
  const answeredQuestionCount = answerDetailsMap.size;
  const correctAnswersCount = (gameSession?.questions || []).reduce(
    (total, sessionQuestion) => total + (didAnswerDetailMatchQuestion(sessionQuestion, answerDetailsMap.get(sessionQuestion.id)) ? 1 : 0),
    0
  );
  const unansweredQuestionCount = Math.max((gameSession?.questions?.length || 0) - answeredQuestionCount, 0);
  const averagePointsPerCorrectAnswer = correctAnswersCount > 0 ? Math.round(score / correctAnswersCount) : 0;
  const currentQuestionTypeLabel = QUESTION_TYPE_LABELS[question?.question_type] || QUESTION_TYPE_LABELS.default;
  const currentQuestionHelper = QUESTION_TYPE_HELPERS[question?.question_type] || QUESTION_TYPE_HELPERS.default;
  const shouldUseSplitMediaLayout = Boolean(question?.media_url && !isImagePinQuestion);
  // Keep a manual escape hatch for the three-second automatic transition.
  const shouldShowMobileActionBar = showResult || (!showResult && (isImagePinQuestion || isShortAnswerQuestion || isNumericRangeQuestion));
  const mobileActionLabel = showResult
    ? (currentIdx < gameSession?.questions?.length - 1 ? "Próxima" : "Ver resultado")
    : (isImagePinQuestion || isNumericRangeQuestion ? "ENVIAR" : "Confirmar resposta");
  const mobileActionDisabled = showResult
    ? false
    : (isImagePinQuestion ? !imagePinSelection : !typedAnswer.trim());
  const answerFeedbackPanelClass = showResult
    ? ((isNumericRangeQuestion ? isNumericRangeCorrect : isShortAnswerQuestion ? isShortAnswerCorrect : isImagePinQuestion ? isImagePinCorrect : selectedAnswer === question?.correct_answer)
      ? "border-green-500/60 bg-green-500/20 text-green-100"
      : "border-red-500/60 bg-red-500/20 text-red-100")
    : "";
  const compactResultTextClass = "text-xs leading-relaxed md:text-sm";
  const questionLength = question?.question?.length || 0;
  const optionLengths = Array.isArray(question?.options) ? question.options.map((option) => option?.length || 0) : [];
  const maxOptionLength = optionLengths.length ? Math.max(...optionLengths) : 0;
  const isLongQuestionText = questionLength > 110;
  const isVeryLongQuestionText = questionLength > 180;
  const hasLongOptionText = maxOptionLength > 42;
  const hasVeryLongOptionText = maxOptionLength > 72;
  const shouldUseCompactQuestionCopy = isLongQuestionText || hasLongOptionText;
  const shouldClampQuestionBlock = isVeryLongQuestionText && !showResult;
  const shouldUseScrollableOptionList = !shouldUseSplitMediaLayout && !showResult && (hasVeryLongOptionText || isVeryLongQuestionText);
  const questionBlockClass = shouldClampQuestionBlock
    ? "space-y-1.5 max-h-[17vh] overflow-y-auto pr-1 md:max-h-none md:overflow-visible md:pr-0"
    : shouldUseCompactQuestionCopy
      ? "space-y-1.5"
      : "space-y-2";
  const questionTitleClass = shouldUseCompactQuestionCopy
    ? "font-nunito text-[15px] font-bold leading-snug text-white md:text-2xl"
    : "font-nunito text-base font-bold leading-snug text-white md:text-2xl";
  const questionHelperClass = shouldUseCompactQuestionCopy
    ? "max-w-2xl text-[11px] leading-relaxed text-zinc-400 md:text-sm"
    : "max-w-2xl text-xs leading-relaxed text-zinc-400 md:text-sm";
  const optionsContainerClass = shouldUseSplitMediaLayout
    ? "grid gap-2.5 lg:grid-cols-2"
    : shouldUseScrollableOptionList
      ? "max-h-[34vh] space-y-2 overflow-y-auto pr-1"
      : "space-y-2";
  const optionButtonClass = hasVeryLongOptionText
    ? "w-full text-left rounded-xl border p-2 transition-all font-outfit text-[13px] md:p-3.5 md:text-base flex items-center gap-2 md:gap-3"
    : hasLongOptionText
      ? "w-full text-left rounded-xl border p-2.5 transition-all font-outfit text-[13px] md:p-3.5 md:text-base flex items-center gap-2.5 md:gap-3"
      : "w-full text-left rounded-xl border p-2.5 transition-all font-outfit text-[14px] md:p-3.5 md:text-base flex items-center gap-2.5 md:gap-3";
  const questionMediaBlock = shouldUseSplitMediaLayout ? (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-zinc-950/50 p-3 lg:h-full">
      {question?.media_type === QUESTION_MEDIA_TYPE_IMAGE ? (
        <img
          src={question.media_url}
          alt={`Midia da pergunta ${currentIdx + 1}`}
          className="max-h-[18vh] w-full rounded-xl object-contain md:max-h-[32vh] lg:h-full lg:max-h-[60vh]"
          loading="lazy"
        />
      ) : null}
      {question?.media_type === QUESTION_MEDIA_TYPE_AUDIO && isDirectAudioUrl(question.media_url) ? (
        <audio controls preload="none" className="w-full">
          <source src={timedQuestionDirectMediaUrl} />
          Seu navegador não suporta áudio embutido.
        </audio>
      ) : null}
      {question?.media_type === QUESTION_MEDIA_TYPE_AUDIO && !isDirectAudioUrl(question.media_url) && timedQuestionYouTubeUrl ? (
        <iframe
          title={`Audio da pergunta ${currentIdx + 1}`}
          src={timedQuestionYouTubeUrl}
          className="h-[18vh] w-full rounded-xl border-0 md:aspect-video md:h-auto"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          loading="lazy"
        />
      ) : null}
      {question?.media_type === QUESTION_MEDIA_TYPE_VIDEO && isDirectVideoUrl(question.media_url) ? (
        <video controls preload="metadata" className="max-h-[18vh] w-full rounded-xl bg-black md:max-h-[32vh] lg:h-full lg:max-h-[60vh]">
          <source src={timedQuestionDirectMediaUrl} />
          Seu navegador não suporta vídeo embutido.
        </video>
      ) : null}
      {question?.media_type === QUESTION_MEDIA_TYPE_VIDEO && !isDirectVideoUrl(question.media_url) && (timedQuestionYouTubeUrl || timedQuestionVimeoUrl) ? (
        <div className="h-[18vh] overflow-hidden rounded-xl md:aspect-video md:h-auto lg:h-full">
          <iframe
            title={`Video da pergunta ${currentIdx + 1}`}
            src={timedQuestionYouTubeUrl || timedQuestionVimeoUrl}
            className="h-full w-full border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
          />
        </div>
      ) : null}
      {question?.media_credit ? (
        <p className="mt-2 text-xs text-zinc-400">Crédito: {question.media_credit}</p>
      ) : null}
    </div>
  ) : null;

  useEffect(() => {
    if (!isNumericRangeQuestion || showResult) return;
    if (numericMinValue === null || numericMaxValue === null) return;
    if (submittedNumericAnswer !== null) return;
    const initialValue = numericRangeMidpoint !== null ? numericRangeMidpoint : numericMinValue;
    setTypedAnswer(formatNumericDisplay(initialValue, numericSliderStep));
  }, [
    currentIdx,
    isNumericRangeQuestion,
    numericMaxValue,
    numericMinValue,
    numericRangeMidpoint,
    numericSliderStep,
    showResult,
    submittedNumericAnswer,
  ]);

  useEffect(() => {
    if (!gameSession || gameEnded || !gameStarted || !isShortAnswerQuestion || isNumericRangeQuestion) return undefined;

    const handleShortAnswerEnter = (event) => {
      if (event.key !== "Enter" || event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) return;
      const target = event.target;
      const tagName = target?.tagName?.toLowerCase?.() || "";
      const isEditableTarget = tagName === "input" || tagName === "textarea";
      if (!isEditableTarget) return;

      event.preventDefault();

      if (showResult) {
        goToNextQuestionRef.current();
        return;
      }

      const trimmedAnswer = typedAnswer.trim();
      if (!trimmedAnswer) return;
      handleAnswerRef.current(-1, trimmedAnswer);
    };

    window.addEventListener("keydown", handleShortAnswerEnter);
    return () => window.removeEventListener("keydown", handleShortAnswerEnter);
  }, [gameEnded, gameSession, gameStarted, isNumericRangeQuestion, isShortAnswerQuestion, showResult, typedAnswer]);

  if (coverParams && (!gameSession || gameEnded)) {
    return <div className="min-h-screen bg-zinc-950 pt-28">
      {preGameCover}
      <AuthModal open={showAuth} onClose={() => setShowAuth(false)} />
      <GamePassPurchaseDialog
        open={showGamePassDialog}
        onClose={() => setShowGamePassDialog(false)}
        wallet={gamePassWallet}
        buyingPackId={buyingGamePassPackId}
        onBuy={buyGamePassPack}
        returnTo={gamePassReturnTo}
      />
    </div>;
  }

  if (gameSession && !gameEnded) {
    return (
      <div ref={protectedQuizRef} className="min-h-screen pt-[4.5rem] pb-24 md:h-[100dvh] md:min-h-[100dvh] md:overflow-hidden md:pb-3 md:pt-[5.5rem]" data-testid="game-active">
        <div className="container mx-auto flex h-full max-w-6xl flex-col px-3 md:px-6">
          <div className="mb-1.5 shrink-0">
            <div className="mb-1.5 flex flex-col gap-1.5 md:mb-2 md:flex-row md:items-center md:justify-between md:gap-2">
              <div className="min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
                  <span className="line-clamp-1 font-nunito text-[15px] font-black leading-tight text-white md:text-xl">
                    {isCommunityQuizSession && gameSession.community_quiz_title
                      ? gameSession.community_quiz_title
                      : gameSession.theme}
                  </span>
                  <span className="font-outfit text-[10px] text-zinc-400 md:text-sm">
                    {gameSession.category}
                  </span>
                  <span className="font-outfit text-[10px] text-zinc-500 md:text-xs">
                    {gameSession.time_per_question || DEFAULT_TIME_PER_QUESTION}s por pergunta
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1 md:gap-2 md:justify-end">
                <span className="font-outfit text-[10px] text-zinc-400 flex items-center gap-1 rounded-full border border-white/10 bg-zinc-900/70 px-1.5 py-0.5 md:text-sm md:px-3 md:py-1">
                  <Users className="h-3.5 w-3.5 md:h-4 md:w-4" />
                  {currentIdx + 1} / {gameSession.questions.length}
                </span>
                <span className="font-outfit text-[10px] text-zinc-400 flex items-center gap-1 rounded-full border border-white/10 bg-zinc-900/70 px-1.5 py-0.5 md:text-sm md:px-3 md:py-1">
                  <Clock className="h-3.5 w-3.5 md:h-4 md:w-4" />
                  {(timeLeftMs / 1000).toFixed(1)}s
                </span>
                <span className="font-nunito font-bold text-xs text-[#fccf1f] flex items-center gap-1 rounded-full border border-[#fccf1f]/20 bg-[#fccf1f]/10 px-1.5 py-0.5 md:text-lg md:px-3 md:py-1">
                  <Trophy className="h-3.5 w-3.5 md:h-4 md:w-4" />
                  {score} pts
                </span>
              </div>
            </div>
            <div className="mb-1.5 h-1 w-full overflow-hidden rounded-full bg-zinc-800 md:mb-2 md:h-1.5">
              <div
                className="h-full bg-[#fccf1f] rounded-full transition-all duration-500"
                style={{ width: `${((currentIdx + 1) / gameSession.questions.length) * 100}%` }}
              />
            </div>
            <div className="h-1 w-full overflow-hidden rounded-full bg-zinc-900 md:h-1.5">
              <div
                className={`h-full rounded-full transition-all duration-75 ${timeProgressPct > 33 ? "bg-emerald-400" : timeProgressPct > 15 ? "bg-amber-400" : "bg-red-400"}`}
                style={{ width: `${timeProgressPct}%` }}
              />
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-hidden rounded-2xl border border-white/10 bg-zinc-900/60 p-3 shadow-[0_24px_80px_rgba(0,0,0,0.28)] backdrop-blur-md md:p-5">
            <div className={shouldUseSplitMediaLayout ? "space-y-4 pb-8 lg:grid lg:grid-cols-[minmax(280px,34%)_minmax(0,1fr)] lg:gap-4 lg:space-y-0 lg:pb-0" : "space-y-4 pb-8 md:pb-0"}>
              {question?.media_url && !isImagePinQuestion ? (
                <div className="overflow-hidden rounded-2xl border border-white/10 bg-zinc-950/40 p-2.5">
                  {question?.media_type === QUESTION_MEDIA_TYPE_IMAGE ? (
                    <img
                      src={question.media_url}
                      alt={`Midia da pergunta ${currentIdx + 1}`}
                      className="max-h-[16vh] w-full rounded-xl object-contain md:max-h-[24vh] lg:max-h-[34vh]"
                      loading="lazy"
                    />
                  ) : null}
                  {question?.media_type === QUESTION_MEDIA_TYPE_AUDIO && isDirectAudioUrl(question.media_url) ? (
                    <audio controls preload="none" className="w-full">
                      <source src={timedQuestionDirectMediaUrl} />
                      {"Seu navegador n\u00E3o suporta \u00E1udio embutido."}
                    </audio>
                  ) : null}
                  {question?.media_type === QUESTION_MEDIA_TYPE_AUDIO && !isDirectAudioUrl(question.media_url) && timedQuestionYouTubeUrl ? (
                    <iframe
                      title={`Audio da pergunta ${currentIdx + 1}`}
                      src={timedQuestionYouTubeUrl}
                      className="h-[16vh] w-full rounded-xl border-0 md:aspect-video md:h-auto"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      loading="lazy"
                    />
                  ) : null}
                  {question?.media_type === QUESTION_MEDIA_TYPE_VIDEO && isDirectVideoUrl(question.media_url) ? (
                    <video controls preload="metadata" className="max-h-[16vh] w-full rounded-xl bg-black md:max-h-[24vh] lg:max-h-[34vh]">
                      <source src={timedQuestionDirectMediaUrl} />
                      {"Seu navegador n\u00E3o suporta v\u00EDdeo embutido."}
                    </video>
                  ) : null}
                  {question?.media_type === QUESTION_MEDIA_TYPE_VIDEO && !isDirectVideoUrl(question.media_url) && (timedQuestionYouTubeUrl || timedQuestionVimeoUrl) ? (
                    <div className="h-[16vh] overflow-hidden rounded-xl md:aspect-video md:h-auto">
                      <iframe
                        title={`Video da pergunta ${currentIdx + 1}`}
                        src={timedQuestionYouTubeUrl || timedQuestionVimeoUrl}
                        className="h-full w-full border-0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        loading="lazy"
                      />
                    </div>
                  ) : null}
                  {question?.media_credit ? (
                    <p className="mt-2 text-xs text-zinc-400">{"Cr\u00E9dito: "}{question.media_credit}</p>
                  ) : null}
                </div>
              ) : null}
              <div className="space-y-3 lg:min-w-0">
                <div className={questionBlockClass}>
                  {isTrueFalseQuestion ? (
                    <p className="text-xs font-black uppercase tracking-[0.14em] text-[#fccf1f] md:text-sm">{"\u00C9 REAL OU FAKE NEWS:"}</p>
                  ) : null}
                  <h2 className={questionTitleClass} data-testid="question-text">
                    {question?.question}
                    <span className="ml-1.5 whitespace-nowrap font-outfit text-[9px] font-medium uppercase tracking-[0.12em] text-zinc-500 md:ml-2 md:text-xs">
                      {currentQuestionTypeLabel}
                    </span>
                  </h2>
                  <p className={questionHelperClass}>
                    {currentQuestionHelper}
                  </p>
                </div>

                <div key={currentIdx} className="space-y-2.5">
                  {isImagePinQuestion ? (
                    <div className="space-y-3">
                      <div className="rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-zinc-300">
                        {"Encontre a \u00E1rea certa na imagem, marque um ponto e confirme quando estiver seguro."}
                      </div>
                      <div className="overflow-hidden rounded-2xl border border-white/10 bg-zinc-950/40 p-3">
                        <div
                          role="button"
                          tabIndex={showResult ? -1 : 0}
                          onClick={handleImagePinSelect}
                          onKeyDown={(event) => {
                            if (showResult) return;
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                            }
                          }}
                          className={`relative overflow-hidden rounded-xl ${showResult ? "" : "cursor-crosshair"}`}
                          data-testid="image-pin-canvas"
                        >
                          <img
                            src={question.media_url}
                            alt={`Imagem da pergunta ${currentIdx + 1}`}
                            className="max-h-[26vh] w-full rounded-xl object-contain md:max-h-[42vh]"
                            loading="lazy"
                          />
                          <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                            {showResult && imagePinPolygon.length >= 3 ? (
                              <polygon
                                points={imagePinPointsToSvg(imagePinPolygon)}
                                fill="rgba(252, 207, 31, 0.22)"
                                stroke="rgba(252, 207, 31, 0.95)"
                                strokeWidth="0.7"
                                vectorEffect="non-scaling-stroke"
                              />
                            ) : null}
                          </svg>
                          {imagePinSelectionPercent ? (
                            <span
                              className={`pointer-events-none absolute h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 shadow-lg ${showResult ? (isImagePinCorrect ? "border-green-300 bg-green-500/80" : "border-red-200 bg-red-500/85") : "border-white bg-[#fccf1f]"}`}
                              style={imagePinSelectionPercent}
                            />
                          ) : null}
                        </div>
                        {question?.media_credit ? (
                          <p className="mt-2 text-xs text-zinc-400">{"Cr\u00E9dito: "}{question.media_credit}</p>
                        ) : null}
                      </div>
                      <p className="text-xs text-zinc-400">
                        Clique em um ponto da imagem e depois em enviar.
                      </p>
                      <div className="hidden md:block">
                        <Button
                          type="button"
                          onClick={() => handleAnswer(-1, "", imagePinSelection)}
                          disabled={showResult || !imagePinSelection}
                          className="w-full bg-[#fccf1f] text-black font-bold hover:bg-[#e6bc1c] disabled:opacity-60"
                        >
                          ENVIAR
                        </Button>
                      </div>
                      {showResult ? (
                        <div className={`rounded-xl border px-3 py-2.5 text-left md:px-4 md:py-3 ${answerFeedbackPanelClass}`}>
                          <p className={compactResultTextClass}><strong>Sua resposta:</strong> {imagePinSelection ? "Ponto marcado na imagem" : "N\u00E3o respondida"}</p>
                          <p className={`mt-1.5 ${compactResultTextClass}`}><strong>Resposta correta:</strong> {question?.answer_text || "Area marcada na imagem"}</p>
                          {question?.explanation ? (
                            <p className={`mt-1.5 ${compactResultTextClass}`}><strong>{"Explica\u00E7\u00E3o:"}</strong> {question.explanation}</p>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  ) : isShortAnswerQuestion || isNumericRangeQuestion ? (
                    <div className="space-y-3">
                      <div className="rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-zinc-300">
                        {isNumericRangeQuestion
                          ? "Use o slider para escolher seu n\u00FAmero final e envie quando quiser travar o palpite."
                          : "Digite sua melhor resposta. Se houver mais de uma forma v\u00E1lida, o sistema compara as varia\u00E7\u00F5es aceitas."}
                      </div>
                      {isNumericRangeQuestion && numericMinValue !== null && numericMaxValue !== null ? (
                        <div className="rounded-2xl border border-white/10 bg-zinc-950/40 px-4 py-5 md:px-6">
                          <div className="relative px-1 pt-8">
                            <div
                              className="pointer-events-none absolute top-0 -translate-x-1/2 rounded-full border border-[#fccf1f]/30 bg-[#fccf1f]/15 px-3 py-1 text-sm font-bold text-[#fccf1f]"
                              style={{ left: `${numericSliderPercent}%` }}
                            >
                              {formatNumericDisplay(numericSliderValue ?? numericMinValue, numericSliderStep)}
                            </div>
                            <input
                              type="range"
                              min={numericMinValue}
                              max={numericMaxValue}
                              step={numericSliderStep}
                              value={numericSliderValue ?? numericMinValue}
                              onChange={(event) => setTypedAnswer(formatNumericDisplay(clampNumericValue(Number(event.target.value), numericMinValue, numericMaxValue), numericSliderStep))}
                              disabled={showResult}
                              className="h-2 w-full cursor-pointer appearance-none rounded-full bg-zinc-700 accent-[#fccf1f] disabled:opacity-60"
                              data-testid="numeric-range-slider"
                            />
                            <div className="mt-3 flex items-center justify-between text-xs font-semibold uppercase tracking-[0.14em] text-zinc-400">
                              <span>{formatNumericDisplay(numericMinValue, numericSliderStep)}</span>
                              <span>{formatNumericDisplay(numericMaxValue, numericSliderStep)}</span>
                            </div>
                          </div>
                        </div>
                      ) : null}
                      <input
                        type={isNumericRangeQuestion ? "number" : "text"}
                        value={typedAnswer}
                        onChange={(event) => setTypedAnswer(event.target.value)}
                        disabled={showResult}
                        placeholder={isNumericRangeQuestion ? "Digite um valor num\u00E9rico" : "Digite sua resposta"}
                        className={`${isNumericRangeQuestion && numericMinValue !== null && numericMaxValue !== null ? "hidden" : "w-full"} rounded-xl border border-white/10 bg-zinc-800/70 px-4 py-3 text-base text-white outline-none transition focus:border-[#fccf1f]/60 disabled:opacity-60`}
                        data-testid="short-answer-input"
                      />
                      {isNumericRangeQuestion ? (
                        <p className="text-xs text-zinc-400">
                          {"Arraste a bolinha at\u00E9 o n\u00FAmero desejado e clique em enviar."}
                        </p>
                      ) : null}
                      {isNumericRangeQuestion ? (
                        <p className="hidden text-xs text-zinc-400">
                          {"Faixa aceita: "}{question?.numeric_min ?? "—"}{" até "}{question?.numeric_max ?? "—"}
                        </p>
                      ) : (
                        <p className="text-xs text-zinc-400">
                          {"O sistema aceita at\u00E9 4 respostas v\u00E1lidas e tolera pequenos erros de digita\u00E7\u00E3o."}
                        </p>
                      )}
                      <div className="hidden md:block">
                        <Button
                          type="button"
                          onClick={() => handleAnswer(-1, typedAnswer.trim())}
                          disabled={showResult || !typedAnswer.trim()}
                          className="w-full bg-[#fccf1f] text-black font-bold hover:bg-[#e6bc1c] disabled:opacity-60"
                        >
                          {isNumericRangeQuestion ? "ENVIAR" : "Confirmar resposta"}
                        </Button>
                      </div>
                      {showResult ? (
                        <div className={`rounded-xl border px-3 py-2.5 text-left md:px-4 md:py-3 ${answerFeedbackPanelClass}`}>
                          <p className={compactResultTextClass}><strong>Sua resposta:</strong> {typedAnswer || "N\u00E3o respondida"}</p>
                          <p className={`mt-1.5 ${compactResultTextClass}`}><strong>Resposta correta:</strong> {question?.answer_text || "N\u00E3o dispon\u00EDvel"}</p>
                          {isNumericRangeQuestion ? (
                            <p className={`mt-1.5 ${compactResultTextClass}`}><strong>{"Faixa dispon\u00EDvel:"}</strong> {question?.numeric_min ?? "—"}{" até "}{question?.numeric_max ?? "—"}</p>
                          ) : acceptedShortAnswers.length > 1 ? (
                            <p className={`mt-1.5 ${compactResultTextClass}`}><strong>Respostas aceitas:</strong> {acceptedShortAnswers.join(" • ")}</p>
                          ) : null}
                          {question?.explanation ? (
                            <p className={`mt-1.5 ${compactResultTextClass}`}><strong>{"Explica\u00E7\u00E3o:"}</strong> {question.explanation}</p>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className={optionsContainerClass}>
                      {question?.options.map((option, idx) => {
                        let style = "bg-zinc-800/50 border-white/10 hover:border-[#fccf1f]/50 hover:bg-zinc-800";
                        if (showResult) {
                          if (idx === question.correct_answer) {
                            style = "bg-green-500/20 border-green-500/60";
                          } else if (idx === selectedAnswer && idx !== question.correct_answer) {
                            style = "bg-red-500/20 border-red-500/60";
                          } else {
                            style = "bg-zinc-800/30 border-white/5 opacity-50";
                          }
                        }

                        return (
                          <button
                            key={idx}
                            onClick={() => handleAnswer(idx)}
                            disabled={showResult}
                            className={`${optionButtonClass} ${style}`}
                            data-testid={`answer-option-${idx}`}
                          >
                            <span
                              className={`h-7 w-7 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 md:h-8 md:w-8 md:text-sm ${
                                showResult && idx === question.correct_answer
                                  ? "bg-green-500 text-white"
                                  : showResult && idx === selectedAnswer
                                    ? "bg-red-500 text-white"
                                    : "bg-zinc-700 text-zinc-400"
                              }`}
                            >
                              {String.fromCharCode(65 + idx)}
                            </span>
                            <span className="text-white leading-snug">{option}</span>
                            {showResult && idx === question.correct_answer && (
                              <Check className="w-5 h-5 text-green-400 ml-auto shrink-0" />
                            )}
                            {showResult && idx === selectedAnswer && idx !== question.correct_answer && (
                              <XIcon className="w-5 h-5 text-red-400 ml-auto shrink-0" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {showResult && question?.explanation ? (
                    <div className="rounded-xl border border-white/10 bg-zinc-950/40 px-3 py-2.5 text-xs leading-relaxed text-zinc-200 md:px-4 md:py-3 md:text-sm">
                      <strong className="text-white">{"Explica\u00E7\u00E3o:"}</strong> {question.explanation}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            {showResult && (
              <div className="mt-4 space-y-2">
                {autoAdvanceSeconds !== null ? (
                  <p
                    className={isApp
                      ? "pointer-events-none fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom,0px)+9.5rem)] z-40 mx-auto w-fit rounded-full border border-white/10 bg-zinc-950/95 px-3 py-1.5 text-center font-outfit text-xs text-zinc-300 shadow-lg backdrop-blur"
                      : "text-center font-outfit text-xs text-zinc-400"}
                    aria-live="polite"
                  >
                    {currentIdx < gameSession.questions.length - 1
                      ? `Próxima pergunta em ${autoAdvanceSeconds}s`
                      : `Resultado final em ${autoAdvanceSeconds}s`}
                  </p>
                ) : null}
                <div className="hidden md:block">
                  <Button
                    type="button"
                    onClick={goToNextQuestion}
                    className="w-full bg-[#fccf1f] text-black font-bold hover:bg-[#e6bc1c]"
                  >
                    {currentIdx < gameSession.questions.length - 1 ? "Pr\u00F3xima" : "Ver resultado"}
                  </Button>
                </div>
                {question?.origin_label ? (
                  <p className="text-center font-outfit text-[10px] uppercase tracking-[0.1em] text-zinc-500 md:text-[11px] md:tracking-[0.12em]">
                    Origem: {question.origin_label}
                  </p>
                ) : null}
                {question?.source_url ? (
                  <a
                    href={question.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex w-full items-center justify-center gap-1.5 text-xs text-zinc-400 transition-colors hover:text-[#fccf1f] md:text-sm md:text-zinc-300"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {"Refer\u00EAncia"}
                  </a>
                ) : null}
              </div>
            )}
          </div>
        </div>
        {shouldShowMobileActionBar ? (
          <div className={`fixed inset-x-0 z-40 border-t border-white/10 bg-zinc-950/96 backdrop-blur md:hidden ${isApp ? "bottom-[calc(env(safe-area-inset-bottom,0px)+5rem)]" : "bottom-0"}`}>
            <div className="container mx-auto max-w-6xl px-3 pb-[calc(env(safe-area-inset-bottom,0px)+0.75rem)] pt-2">
              <Button
                type="button"
                onClick={() => {
                  if (showResult) {
                    goToNextQuestion();
                    return;
                  }
                  if (isImagePinQuestion) {
                    handleAnswer(-1, "", imagePinSelection);
                    return;
                  }
                  handleAnswer(-1, typedAnswer.trim());
                }}
                disabled={mobileActionDisabled}
                className="w-full bg-[#fccf1f] text-black font-bold hover:bg-[#e6bc1c] disabled:opacity-60"
              >
                {mobileActionLabel}
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  if (gameSession && gameEnded) {
    return (
      <div ref={protectedQuizRef} className="min-h-screen pt-28 pb-16" data-testid="game-ended">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="bg-zinc-900/60 backdrop-blur-md border border-white/10 rounded-2xl p-6 text-center md:p-10">
            <Trophy className="w-16 h-16 text-[#fccf1f] mx-auto mb-6" />
            <p className="font-outfit text-xs uppercase tracking-[0.2em] text-[#fccf1f] mb-2">
              Resultado final
            </p>
            <h2 className="font-nunito font-bold text-3xl text-white mb-2">Jogo Finalizado!</h2>
            {isCommunityQuizSession && gameSession.community_quiz_title ? (
              <p className="font-nunito text-xl font-bold text-zinc-100 mb-2">{gameSession.community_quiz_title}</p>
            ) : null}
            <p className="font-outfit text-zinc-400 mb-6">{gameSession.category} — {gameSession.theme}</p>

            <div className="font-nunito font-black text-6xl text-[#fccf1f] mb-2" data-testid="final-score">
              {score} pts
            </div>
            <p className="font-outfit text-sm text-zinc-500 mb-2">
              de {maxPossibleScore} pts possíveis
            </p>
            <p className="font-outfit text-lg text-zinc-300 mb-8">
              {scorePct === 100
                ? "Perfeito! Você é um verdadeiro Lorde!"
                : scorePct >= 70
                  ? "Muito bem! Continue assim!"
                  : scorePct >= 50
                    ? "Bom trabalho! Pratique mais!"
                    : "Continue estudando e tente novamente!"}
            </p>

            <div className="mb-8 grid gap-3 text-left sm:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                <p className="font-outfit text-[11px] uppercase tracking-[0.18em] text-zinc-500">Acertos</p>
                <p className="mt-2 font-nunito text-2xl font-black text-white">{correctAnswersCount}</p>
                <p className="font-outfit text-sm text-zinc-400">de {gameSession.questions.length} perguntas</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                <p className="font-outfit text-[11px] uppercase tracking-[0.18em] text-zinc-500">Aproveitamento</p>
                <p className="mt-2 font-nunito text-2xl font-black text-white">{scorePct}%</p>
                <p className="font-outfit text-sm text-zinc-400">pontuacao total obtida</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                <p className="font-outfit text-[11px] uppercase tracking-[0.18em] text-zinc-500">Media por acerto</p>
                <p className="mt-2 font-nunito text-2xl font-black text-white">{averagePointsPerCorrectAnswer}</p>
                <p className="font-outfit text-sm text-zinc-400">pontos por resposta certa</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                <p className="font-outfit text-[11px] uppercase tracking-[0.18em] text-zinc-500">Não respondidas</p>
                <p className="mt-2 font-nunito text-2xl font-black text-white">{unansweredQuestionCount}</p>
                <p className="font-outfit text-sm text-zinc-400">perguntas sem envio</p>
              </div>
            </div>

            {submitting && (
              <p className="text-zinc-400 text-sm mb-4 flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-[#fccf1f] border-t-transparent rounded-full animate-spin" />
                Salvando pontuação...
              </p>
            )}

            <div className="rounded-2xl border border-white/10 bg-black/20 p-4 sm:p-5 md:p-6 text-left mb-6">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-4">
                <div>
                  <h3 className="font-nunito font-bold text-xl text-white">Como foi este quiz?</h3>
                  <p className="font-outfit text-sm text-zinc-300 dark:text-zinc-400">
                    Sua nota ajuda a gente a lapidar a seleção de perguntas e o ritmo do jogo.
                  </p>
                </div>
                {feedbackSaved && (
                  <span className="inline-flex items-center gap-2 rounded-full border border-green-500/30 bg-green-500/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-green-300">
                    <Check className="w-3.5 h-3.5" />
                    Feedback salvo
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFeedbackRating(value)}
                    title={`${value} estrela${value > 1 ? "s" : ""}`}
                    aria-label={`${value} estrela${value > 1 ? "s" : ""}`}
                    className={`inline-flex items-center justify-center rounded-full border p-3 transition-colors ${
                      feedbackRating >= value
                        ? "border-[#fccf1f]/70 bg-[#fccf1f]/15 text-[#fccf1f]"
                        : "border-white/10 bg-zinc-900 text-zinc-400 hover:border-[#fccf1f]/30 hover:text-zinc-200"
                    }`}
                  >
                    <Star className={`w-5 h-5 ${feedbackRating >= value ? "fill-current" : ""}`} />
                  </button>
                ))}
              </div>

              <textarea
                value={feedbackComment}
                onChange={(event) => setFeedbackComment(event.target.value)}
                rows={3}
                placeholder="Se quiser, deixe um comentário rápido sobre este quiz."
                className="w-full rounded-xl border border-white/10 bg-zinc-950/80 px-4 py-3 font-outfit text-sm text-white placeholder:text-zinc-500 focus:border-[#fccf1f]/40 focus:outline-none light:bg-white light:text-zinc-900 light:placeholder:text-zinc-400"
              />

              <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                    onClick={() => setPostGameMode((current) => (current === "question-review" ? null : "question-review"))}
                  >
                    Avaliar perguntas / Reportar erros
                  </Button>
                </div>

                <Button
                  type="button"
                  onClick={submitOverallFeedback}
                  disabled={feedbackSubmitting || feedbackSaved}
                  className="bg-[#fccf1f] text-black font-bold hover:bg-[#e6bc1c]"
                >
                  {feedbackSubmitting ? "Enviando..." : feedbackSaved ? "Feedback enviado" : "Enviar feedback"}
                </Button>
              </div>
            </div>

                        {postGameMode === "question-review" && (
              <div className="rounded-2xl border border-white/10 bg-black/20 p-4 sm:p-5 md:p-6 text-left mb-6">
                <h3 className="font-nunito font-bold text-xl text-white mb-2">Avaliar perguntas / Reportar erros</h3>
                <p className="font-outfit text-sm text-zinc-300 dark:text-zinc-400 mb-4">
                  Marque quais perguntas funcionaram melhor para você e, se encontrar algo errado, envie o report no mesmo card.
                </p>

                <div className="space-y-3">
                  {gameSession.questions.map((gameQuestion, index) => {
                    const likeKey = `${gameQuestion.id}:like`;
                    const dislikeKey = `${gameQuestion.id}:dislike`;
                    const submitKey = `${gameQuestion.id}:report_error`;
                    const likeSaved = questionFeedbackState[likeKey]?.success;
                    const dislikeSaved = questionFeedbackState[dislikeKey]?.success;
                    const savedReport = questionFeedbackState[submitKey]?.success;
                    const likeLoading = questionFeedbackSubmitting[likeKey];
                    const dislikeLoading = questionFeedbackSubmitting[dislikeKey];
                    const isSubmittingReport = questionFeedbackSubmitting[submitKey];
                    const selectedErrorType = reportSelections[gameQuestion.id] || "";
                    const comment = reportComments[gameQuestion.id] || "";
                    const isReportOpen = Boolean(openQuestionReports[gameQuestion.id]);

                    return (
                      <div key={gameQuestion.id} className="rounded-xl border border-white/10 bg-zinc-950/70 p-3 sm:p-4 light:bg-white/95">
                        <div>
                          <div>
                            <p className="font-outfit text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">
                              Pergunta {index + 1}
                            </p>
                            <p className="font-nunito font-bold text-white leading-relaxed">{gameQuestion.question}</p>
                            <p className="font-outfit text-sm text-zinc-300 dark:text-zinc-400 mt-1.5">
                              Resposta certa:{" "}
                              <span className="text-zinc-200 font-medium">
                                {gameQuestion.answer_text || gameQuestion.options?.[gameQuestion.correct_answer] || "Não disponível"}
                              </span>
                            </p>
                          </div>
                          <p className="font-outfit text-[11px] text-zinc-500 mt-1.5">
                            Origem: {gameQuestion.origin_label}
                          </p>
                        </div>

                        <div className="mt-2.5 flex flex-col gap-2.5 border-t border-white/10 pt-3 sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            disabled={likeLoading}
                            onClick={() => submitQuestionFeedback({ questionId: gameQuestion.id, feedbackType: "like" })}
                            className={`h-9 w-9 rounded-full p-0 ${likeSaved ? "border-green-500/40 text-green-300 bg-green-500/10 hover:bg-green-500/15" : "border-zinc-700 text-zinc-300 hover:bg-zinc-800 light:border-zinc-300 light:text-zinc-700 light:hover:bg-zinc-100"}`}
                            title={likeSaved ? "Curtida salva" : "Gostei"}
                            aria-label={likeSaved ? "Curtida salva" : "Gostei"}
                          >
                            <ThumbsUp className="w-4 h-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            disabled={dislikeLoading}
                            onClick={() => submitQuestionFeedback({ questionId: gameQuestion.id, feedbackType: "dislike" })}
                            className={`h-9 w-9 rounded-full p-0 ${dislikeSaved ? "border-red-500/40 text-red-300 bg-red-500/10 hover:bg-red-500/15" : "border-zinc-700 text-zinc-300 hover:bg-zinc-800 light:border-zinc-300 light:text-zinc-700 light:hover:bg-zinc-100"}`}
                            title={dislikeSaved ? "Registro salvo" : "Não gostei"}
                            aria-label={dislikeSaved ? "Registro salvo" : "Não gostei"}
                          >
                            <ThumbsDown className="w-4 h-4" />
                          </Button>
                          <div className="min-h-[18px] text-[11px] text-zinc-300 dark:text-zinc-400">
                            {likeLoading || dislikeLoading
                              ? "Salvando..."
                              : likeSaved
                                ? "Curtida salva"
                                : dislikeSaved
                                  ? "Registro salvo"
                                  : ""}
                          </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              disabled={savedReport}
                              onClick={() =>
                                setOpenQuestionReports((current) => ({
                                  ...current,
                                  [gameQuestion.id]: !current[gameQuestion.id],
                                }))
                              }
                              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 light:border-zinc-300 light:text-zinc-700 light:hover:bg-zinc-100"
                            >
                              {savedReport ? "Erro reportado" : isReportOpen ? "Fechar report" : "Reportar um erro"}
                            </Button>
                          </div>
                        </div>
                        {isReportOpen && !savedReport && (
                          <div className="mt-3 grid gap-3 md:grid-cols-[150px_minmax(0,1fr)_auto] md:items-start">
                            <select
                              value={selectedErrorType}
                              onChange={(event) =>
                                setReportSelections((current) => ({ ...current, [gameQuestion.id]: event.target.value }))
                              }
                              className="rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white focus:border-[#fccf1f]/40 focus:outline-none light:bg-white light:text-zinc-900"
                            >
                              <option value="">Selecione o tipo do erro</option>
                              <option value="wrong_answer">Gabarito incorreto</option>
                              <option value="ambiguous_question">Pergunta ambígua</option>
                              <option value="wrong_option">Alternativa errada</option>
                              <option value="factual_error">Erro factual</option>
                              <option value="other">Outro</option>
                            </select>

                            <textarea
                              rows={2}
                              value={comment}
                              onChange={(event) =>
                                setReportComments((current) => ({ ...current, [gameQuestion.id]: event.target.value }))
                              }
                              placeholder="Descreva o problema, se quiser."
                              className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:border-[#fccf1f]/40 focus:outline-none light:bg-white light:text-zinc-900 light:placeholder:text-zinc-400"
                            />

                            <Button
                              type="button"
                              disabled={!selectedErrorType || isSubmittingReport}
                              onClick={() =>
                                submitQuestionFeedback({
                                  questionId: gameQuestion.id,
                                  feedbackType: "report_error",
                                  errorType: selectedErrorType,
                                  comment,
                                })
                              }
                              className="bg-[#fccf1f] text-black font-bold hover:bg-[#e6bc1c] md:self-stretch"
                            >
                              {isSubmittingReport ? "Enviando..." : "Enviar report"}
                            </Button>
                          </div>
                          )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="rounded-2xl border border-white/10 bg-black/20 p-4 sm:p-5 md:p-6 text-left mb-6">
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <h3 className="font-nunito font-bold text-xl text-white">Que tal mostrar seus conhecimentos AO VIVO?</h3>
                  <p className="font-outfit text-sm text-zinc-300 dark:text-zinc-400">
                    Confira os próximos eventos presenciais e encontre sua próxima mesa de quiz.
                  </p>
                </div>
                <Link to="/eventos" className="text-sm text-[#fccf1f] hover:text-[#ffe37d] transition-colors">
                  Ver agenda
                </Link>
              </div>

              {loadingRecommendations ? (
                <div className="flex items-center gap-2 text-sm text-zinc-300 dark:text-zinc-400">
                  <span className="w-4 h-4 border-2 border-[#fccf1f] border-t-transparent rounded-full animate-spin" />
                  Buscando eventos recomendados...
                </div>
              ) : recommendedEvents.length > 0 ? (
                <div className="grid gap-3 md:grid-cols-3">
                  {recommendedEvents.map((event) => (
                    <div key={event.id} className="rounded-2xl border border-white/10 bg-zinc-950/80 overflow-hidden">
                      <div className="aspect-[16/9] bg-zinc-900 overflow-hidden">
                        {event.image_url ? (
                          <img src={event.image_url} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <div className="h-full w-full flex items-center justify-center text-zinc-600">
                            <MapPin className="w-8 h-8" />
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <p className="font-outfit text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
                          {event.quiz_type || event.event_format || "Evento presencial"}
                        </p>
                        <h4 className="font-nunito font-bold text-white leading-tight mb-2">{event.title}</h4>
                        <p className="font-outfit text-sm text-zinc-400 mb-3">
                          {formatDateBR(event.date)}
                        </p>
                        <Link to={event.slug ? `/eventos/${event.slug}` : "/eventos"}>
                          <Button className="w-full bg-[#fccf1f] text-black font-bold hover:bg-[#e6bc1c]">
                            Ver evento
                          </Button>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-white/10 bg-zinc-950/60 p-4 text-sm text-zinc-300 dark:text-zinc-400">
                  Ainda não encontramos um evento perfeito para esse tema, mas a agenda completa já está aberta para você explorar.
                </div>
              )}
            </div>

            {gameSession?.community_quiz_id ? (
              <div className="rounded-2xl border border-white/10 bg-black/20 p-4 sm:p-5 md:p-6 text-left mb-6">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <h3 className="font-nunito font-bold text-xl text-white">Ranking deste quiz</h3>
                    <p className="font-outfit text-sm text-zinc-300 dark:text-zinc-400">
                      Veja quem está liderando este quiz da comunidade.
                    </p>
                  </div>
                </div>

                {communityQuizLeaderboard.length > 0 ? (
                  <div className="space-y-2">
                    {communityQuizLeaderboard.map((entry) => (
                      <div key={`${gameSession.community_quiz_id}-${entry.user_id || entry.rank}`} className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-zinc-950/70 px-4 py-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="w-8 text-center font-bold text-zinc-500">#{entry.rank}</span>
                          {entry.picture ? (
                            <img src={entry.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
                          ) : (
                            <div className="w-9 h-9 rounded-full bg-[#fccf1f]/15 border border-[#fccf1f]/20 flex items-center justify-center">
                              <Trophy className="w-4 h-4 text-[#fccf1f]" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="font-nunito font-bold text-white truncate">{entry.display_name}</p>
                            <p className="font-outfit text-xs text-zinc-500">{entry.total_plays || 0} jogadas</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-nunito font-black text-lg text-[#fccf1f]">{entry.best_score} pts</p>
                          <p className="font-outfit text-xs text-zinc-500">melhor pontuação</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-white/10 bg-zinc-950/60 p-4 text-sm text-zinc-300 dark:text-zinc-400">
                    Este quiz ainda não tem jogadores suficientes para formar um ranking.
                  </div>
                )}
              </div>
            ) : null}
            {gameSession?.community_quiz_id ? (
              <div className="rounded-2xl border border-white/10 bg-black/20 p-4 sm:p-5 md:p-6 text-left mb-6">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <h3 className="font-nunito font-bold text-xl text-white">Comentários da comunidade</h3>
                    <p className="font-outfit text-sm text-zinc-300 dark:text-zinc-400">
                      Veja o que outros jogadores acharam deste quiz.
                    </p>
                  </div>
                </div>

                {communityQuizFeedback?.comments_preview?.length ? (
                  <div className="space-y-3">
                    {communityQuizFeedback.comments_preview.map((comment) => (
                      <div key={`${gameSession.community_quiz_id}-${comment.id}`} className="rounded-xl border border-white/10 bg-zinc-950/70 px-4 py-3">
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <p className="font-nunito font-bold text-white truncate">{comment.display_name}</p>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#fccf1f]">
                              {comment.rating ? `${comment.rating}/5` : "Comentário"}
                            </span>
                            {isAdmin ? (
                              <button
                                type="button"
                                onClick={() => deleteCommunityComment(comment.id)}
                                disabled={commentActionSubmitting[`delete-comment:${comment.id}`]}
                                className="text-xs text-red-300 hover:text-red-200 disabled:opacity-60"
                              >
                                Excluir
                              </button>
                            ) : user && comment.user_id !== user.id ? (
                              <button
                                type="button"
                                onClick={() => reportCommunityComment(comment.id)}
                                disabled={commentActionSubmitting[`report-comment:${comment.id}`]}
                                className="text-xs text-zinc-400 hover:text-zinc-200 disabled:opacity-60"
                              >
                                Reportar
                              </button>
                            ) : null}
                          </div>
                        </div>
                        <p className="font-outfit text-sm text-zinc-300">{comment.comment}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-white/10 bg-zinc-950/60 p-4 text-sm text-zinc-300 dark:text-zinc-400">
                    Ainda não existem comentários públicos sobre este quiz.
                  </div>
                )}
              </div>
            ) : null}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => {
                  if (gameSession?.special_event_id) {
                    beginSpecialGame(gameSession.special_event_id);
                    return;
                  }
                  if (gameSession?.community_quiz_id) {
                    startCommunityQuiz(gameSession.community_quiz_id);
                    return;
                  }
                  startGame(
                    gameSession.category_id || categories.find((cat) => cat.name === gameSession.category)?.id,
                    gameSession.theme
                  );
                }}
                className="bg-[#fccf1f] text-black font-nunito font-bold uppercase hover:bg-[#e6bc1c] hover:scale-[1.02] transition-all shadow-[0_0_20px_rgba(252,207,31,0.2)] px-8"
                data-testid="play-again-btn"
              >
                <Play className="w-4 h-4 mr-2" /> Jogar Novamente
              </Button>
              <Button
                onClick={() => {
                  resetGame();
                  setShowLeaderboard(true);
                }}
                variant="outline"
                className="border-[#fccf1f] text-[#fccf1f] hover:bg-[#fccf1f] hover:text-black px-8"
                data-testid="view-ranking-btn"
              >
                <Trophy className="w-4 h-4 mr-2" /> Ranking
              </Button>
              <Button
                onClick={resetGame}
                variant="outline"
                className="border-zinc-600 text-zinc-400 hover:bg-zinc-800 px-8"
                data-testid="back-to-categories-btn"
              >
                Escolher Tema
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showLeaderboard) {
    return (
      <div className="min-h-screen pt-28 pb-16">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <Button onClick={() => setShowLeaderboard(false)} variant="outline" className="mb-6 border-zinc-600 text-zinc-400 hover:bg-zinc-800">
            <ArrowLeft className="w-4 h-4 mr-2" /> Voltar aos Jogos
          </Button>
          <Leaderboard limit={20} />
        </div>
      </div>
    );
  }

  if (isApp) {
    return (
      <>
        <AppGamesHub
          allThemes={allThemes}
          appGamesPanel={appGamesPanel}
          communityQuizzes={communityQuizzes}
          eventNeedsTicket={eventNeedsTicket}
          eventUsesGamePass={eventUsesGamePass}
          getGamePassCost={getGamePassCost}
          gamesRemainingToday={gamesRemainingToday}
          getOnlineEventPath={getOnlineEventPath}
          isUnlimitedDay={isUnlimitedDay}
          navigate={navigate}
          onlineEvents={onlineEvents}
          openOnlineEventPurchase={openOnlineEventPurchase}
          presencialEvents={presencialEvents}
          premiumNewCount={premiumNewCount}
          loadMoreSpecialGames={loadMoreSpecialGames}
          setAppGamesPanel={setAppGamesPanel}
          setShowAuth={setShowAuth}
          setSpecialGamesQuery={setSpecialGamesQuery}
          startGame={startGame}
          startCommunityQuiz={startCommunityQuiz}
          startPremiumGame={startPremiumGame}
          startSpecialGame={startSpecialGame}
          specialEvents={specialEvents}
          specialGamesHasMore={specialGamesHasMore}
          specialGamesLoading={specialGamesLoading}
          specialGamesQuery={specialGamesQuery}
          unseenPresencialCount={unseenPresencialCount}
          user={user}
          userCanAccessOnlineEvent={userCanAccessOnlineEvent}
          hasAvailableGamePassForEvent={hasAvailableGamePassForEvent}
        />
        <GamePassPurchaseDialog
          open={showGamePassDialog}
          onClose={() => setShowGamePassDialog(false)}
          wallet={gamePassWallet}
          buyingPackId={buyingGamePassPackId}
          onBuy={buyGamePassPack}
          returnTo={gamePassReturnTo}
        />
        <AuthModal open={showAuth} onClose={() => setShowAuth(false)} />
      </>
    );
  }

  return (
    <div className="min-h-screen" data-testid="games-page">
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(circle at 50% 30%, rgba(252,207,31,0.12) 0%, transparent 60%)" }} />
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <h1
                className="font-nunito font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#fccf1f] uppercase mb-3"
                style={{ textShadow: "0 0 20px rgba(252,207,31,0.2)" }}
                data-testid="games-title"
              >
                Jogos Online
              </h1>
              <p className="font-outfit text-base md:text-lg text-zinc-400 max-w-2xl">
                Escolha um tema, veja quantas perguntas te esperam e comece a jogar sem passar por etapas extras.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <Button
                onClick={() => setShowLeaderboard(true)}
                variant="outline"
                className="border-[#fccf1f] text-[#fccf1f] hover:bg-[#fccf1f] hover:text-black"
                data-testid="toggle-leaderboard-btn"
              >
                <Trophy className="w-4 h-4 mr-2" /> Ranking
              </Button>
              {!authLoading && !user && (
                <Button
                  onClick={() => setShowAuth(true)}
                  className="bg-[#fccf1f] text-black font-nunito font-bold uppercase hover:bg-[#e6bc1c]"
                  data-testid="games-login-btn"
                >
                  Entrar para Jogar
                </Button>
              )}
            </div>
          </div>

          {user && (
            <div className="bg-zinc-900/60 backdrop-blur-md border border-white/10 rounded-xl p-4 mb-10 flex flex-wrap items-center justify-between gap-4" data-testid="user-game-info">
              <div className="flex items-center gap-3">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[#fccf1f]/20 flex items-center justify-center">
                    <Gamepad2 className="w-5 h-5 text-[#fccf1f]" />
                  </div>
                )}
                <div>
                  <p className="font-nunito font-bold text-white">{user.name}</p>
                  <p className="font-outfit text-xs text-zinc-500">
                    {user.total_score || 0} pts · {user.total_games || 0} jogos
                    {user.subscription_plan !== "free" && (
                      <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                        user.subscription_plan === "master" ? "bg-purple-500/20 text-purple-400" : "bg-[#fccf1f]/20 text-[#fccf1f]"
                      }`}>
                        {user.subscription_plan === "master" ? "Lorde Supremo" : "Pro"}
                      </span>
                    )}
                  </p>
                </div>
              </div>
              {user.subscription_plan === "free" && (
                <Link to="/clube">
                  <Button size="sm" className="bg-[#fccf1f]/10 text-[#fccf1f] border border-[#fccf1f]/30 hover:bg-[#fccf1f] hover:text-black font-outfit text-xs">
                    <Crown className="w-3 h-3 mr-1" /> Upgrade para mais jogos
                  </Button>
                </Link>
              )}
            </div>
          )}

          <GamesCatalogSections
            allThemes={allThemes}
            commentActionSubmitting={commentActionSubmitting}
            communityQuizzes={communityQuizzes}
            deleteCommunityComment={deleteCommunityComment}
            gamesGridConfig={gamesGridConfig}
            isAdmin={isAdmin}
            loading={loading}
            orderedGameSections={orderedGameSections}
            eventNeedsTicket={eventNeedsTicket}
            eventUsesGamePass={eventUsesGamePass}
            getGamePassCost={getGamePassCost}
            getOnlineEventPath={getOnlineEventPath}
            openOnlineEventPurchase={openOnlineEventPurchase}
            presencialEvents={presencialEvents}
            premiumEvents={onlineEvents}
            reportCommunityComment={reportCommunityComment}
            resolveThemeIcon={(themeName) => {
              const iconName = getThemeIconName(themeName, gamesGridConfig.themeIcons);
              return ICON_COMPONENTS[iconName] || Star;
            }}
            setShowAuth={setShowAuth}
            setSpecialGamesQuery={setSpecialGamesQuery}
            startCommunityQuiz={startCommunityQuiz}
            startPremiumGame={startPremiumGame}
            startSpecialGame={startSpecialGame}
            startGame={startGame}
            specialEvents={specialEvents}
            specialGamesHasMore={specialGamesHasMore}
            specialGamesLoading={specialGamesLoading}
            specialGamesQuery={specialGamesQuery}
            specialGamesTotal={specialGamesTotal}
            themeGridClasses={themeGridClasses}
            user={user}
            userCanAccessOnlineEvent={userCanAccessOnlineEvent}
            hasAvailableGamePassForEvent={hasAvailableGamePassForEvent}
            loadMoreSpecialGames={loadMoreSpecialGames}
          />
        </div>
      </section>

      <AuthModal open={showAuth} onClose={() => setShowAuth(false)} />
      <GamePassPurchaseDialog
        open={showGamePassDialog}
        onClose={() => setShowGamePassDialog(false)}
        wallet={gamePassWallet}
        buyingPackId={buyingGamePassPackId}
        onBuy={buyGamePassPack}
        returnTo={gamePassReturnTo}
      />
    </div>
  );
}

export default function GamesPage() {
  return (
    <GamesPageErrorBoundary>
      <GamesPageInner />
    </GamesPageErrorBoundary>
  );
}
